import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { 
  Calendar, Instagram, Mail, Menu, X, DollarSign, ArrowRight, Check, 
  Camera, Send, ChevronDown, MapPin, Clock, Sparkles,
  FileText, Image, Printer, Download, Frame,
  Upload, Trash2, LogOut, Users, CreditCard, User,
  ChevronUp, TrendingUp, BarChart3, ShoppingCart, Plus, Minus,
  FolderOpen, Eye, Megaphone, Package, AlertTriangle, Bell,
  Lock, Unlock, ZoomIn, Loader2, MousePointer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { Toaster, toast } from "sonner";
import { format } from "date-fns";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Stripe publishable key from environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://instagram.com/sheedtyme2pics";
const INSTAGRAM_USERNAME = "sheedtyme2pics";
const CASHAPP_TAG = "$sheedtyme2pics";
// Photographer profile photo
const PHOTOGRAPHER_PHOTO = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/profile_photographer.jpg`;

// Cart Context
const CartContext = React.createContext();

const useCart = () => React.useContext(CartContext);

const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  
  const addItem = (item) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === item.id && i.print_size === item.print_size);
      if (existing) {
        return prev.map(i => i.id === item.id && i.print_size === item.print_size ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    toast.success("Added to cart");
  };
  
  const removeItem = (itemId, printSize) => {
    setItems(prev => prev.filter(i => !(i.id === itemId && i.print_size === printSize)));
  };
  
  const updateQuantity = (itemId, printSize, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId, printSize);
    } else {
      setItems(prev => prev.map(i => i.id === itemId && i.print_size === printSize ? { ...i, quantity } : i));
    }
  };
  
  const clearCart = () => setItems([]);
  
  const total = items.reduce((sum, item) => sum + (item.price * item.quantity) + (item.add_frame ? 5 * item.quantity : 0), 0);
  
  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, total }}>
      {children}
    </CartContext.Provider>
  );
};

// Logo
const Logo = ({ size = "default" }) => {
  const isSmall = size === "small";
  return (
    <div className="flex items-center gap-2" data-testid="logo">
      <div className={`relative ${isSmall ? 'w-8 h-8' : 'w-10 h-10 md:w-12 md:h-12'}`}>
        <div className="absolute inset-0 border-2 border-black rounded-full" />
        <div className="absolute inset-1 bg-black rounded-full flex items-center justify-center">
          <Camera className={`${isSmall ? 'w-4 h-4' : 'w-5 h-5 md:w-6 md:h-6'} text-white`} strokeWidth={1.5} />
        </div>
        <div className={`absolute ${isSmall ? '-bottom-0.5 -right-0.5 w-2.5 h-2.5' : '-bottom-1 -right-1 w-3 h-3'} bg-black rounded-full border-2 border-white`} />
      </div>
      <div className="flex flex-col leading-none">
        <span className={`font-serif ${isSmall ? 'text-base' : 'text-lg md:text-xl'} tracking-tight font-medium`}>Sheedtyme<span className="text-neutral-400">2</span>pics</span>
        <span className={`${isSmall ? 'text-[8px]' : 'text-[9px] md:text-[10px]'} uppercase tracking-[0.2em] text-neutral-500 font-medium`}>Photography & Media</span>
      </div>
    </div>
  );
};

// Announcement Banner
const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  
  useEffect(() => {
    axios.get(`${API}/announcements`).then(res => setAnnouncements(res.data)).catch(() => {});
  }, []);
  
  if (!announcements.length) return null;
  
  const ann = announcements[0];
  return (
    <div style={{ backgroundColor: ann.bg_color, color: ann.text_color }} className="py-2 px-4 text-center text-sm">
      <Megaphone className="w-4 h-4 inline mr-2" />
      {ann.message}
    </div>
  );
};

// Cart Sheet
const CartSheet = () => {
  const { items, removeItem, updateQuantity, total, clearCart } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "" });
  const [loading, setLoading] = useState(false);
  
  const handleCheckout = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.address || !customerInfo.city || !customerInfo.state || !customerInfo.zip_code) {
      toast.error("Please fill in all shipping details");
      return;
    }
    
    setLoading(true);
    try {
      const cartItems = items.map(item => ({
        item_type: item.type,
        item_id: item.id,
        album_id: item.album_id,
        photo_id: item.photo_id,
        quantity: item.quantity,
        print_size: item.print_size,
        add_frame: item.add_frame
      }));
      
      const response = await axios.post(`${API}/cart/checkout`, {
        customer: customerInfo,
        items: cartItems,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button className="relative p-2 hover:bg-neutral-100 rounded" data-testid="cart-btn">
          <ShoppingCart className="w-5 h-5" />
          {items.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-black text-white text-xs rounded-full flex items-center justify-center">
              {items.reduce((sum, i) => sum + i.quantity, 0)}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-serif text-xl">Shopping Cart</SheetTitle>
        </SheetHeader>
        
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-neutral-500">
            <ShoppingCart className="w-12 h-12 mb-4" />
            <p>Your cart is empty</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {items.map((item, idx) => (
              <div key={idx} className="flex gap-4 border-b pb-4">
                <div className="w-16 h-16 bg-neutral-100 overflow-hidden">
                  {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{item.name}</p>
                  <p className="text-xs text-neutral-500">{item.print_size?.replace("print-", "") || "Digital"}</p>
                  {item.add_frame && <p className="text-xs text-neutral-500">+ Frame</p>}
                  <p className="text-sm font-serif">${((item.price + (item.add_frame ? 5 : 0)) * item.quantity).toFixed(2)}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button onClick={() => removeItem(item.id, item.print_size)} className="text-neutral-400 hover:text-black">
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(item.id, item.print_size, item.quantity - 1)} className="w-6 h-6 border flex items-center justify-center">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-sm w-6 text-center">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.print_size, item.quantity + 1)} className="w-6 h-6 border flex items-center justify-center">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="border-t pt-4">
              <div className="flex justify-between text-lg font-medium mb-4">
                <span>Total</span>
                <span className="font-serif">${total.toFixed(2)}</span>
              </div>
              
              {!checkoutOpen ? (
                <Button onClick={() => setCheckoutOpen(true)} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none">
                  Proceed to Checkout
                </Button>
              ) : (
                <div className="space-y-3">
                  <h4 className="font-medium">Shipping Information</h4>
                  <Input placeholder="Full Name *" value={customerInfo.name} onChange={e => setCustomerInfo({...customerInfo, name: e.target.value})} className="rounded-none" />
                  <Input placeholder="Email *" type="email" value={customerInfo.email} onChange={e => setCustomerInfo({...customerInfo, email: e.target.value})} className="rounded-none" />
                  <Input placeholder="Phone" value={customerInfo.phone} onChange={e => setCustomerInfo({...customerInfo, phone: e.target.value})} className="rounded-none" />
                  <Input placeholder="Street Address *" value={customerInfo.address} onChange={e => setCustomerInfo({...customerInfo, address: e.target.value})} className="rounded-none" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City *" value={customerInfo.city} onChange={e => setCustomerInfo({...customerInfo, city: e.target.value})} className="rounded-none" />
                    <Input placeholder="State *" value={customerInfo.state} onChange={e => setCustomerInfo({...customerInfo, state: e.target.value})} className="rounded-none" />
                    <Input placeholder="ZIP *" value={customerInfo.zip_code} onChange={e => setCustomerInfo({...customerInfo, zip_code: e.target.value})} className="rounded-none" />
                  </div>
                  <Button onClick={handleCheckout} disabled={loading} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none">
                    {loading ? "Processing..." : `Pay $${total.toFixed(2)}`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Header
const Header = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { items } = useCart();

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? "bg-white/95 backdrop-blur-md border-b border-neutral-100" : "bg-white"}`}>
      <AnnouncementBanner />
      <div className="flex justify-between items-center py-4 px-6 md:px-12 max-w-screen-2xl mx-auto">
        <a href="/"><Logo /></a>
        
        <nav className="hidden lg:flex items-center gap-6">
          <a href="#services" className="text-xs uppercase tracking-[0.15em] font-medium hover:text-neutral-500">Services</a>
          <a href="#portfolio" className="text-xs uppercase tracking-[0.15em] font-medium hover:text-neutral-500">Portfolio</a>
          <a href="#booking" className="text-xs uppercase tracking-[0.15em] font-medium hover:text-neutral-500">Book Now</a>
          <a href="#shop" className="text-xs uppercase tracking-[0.15em] font-medium hover:text-neutral-500">Shop</a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-neutral-500"><Instagram className="w-4 h-4" /></a>
          <CartSheet />
          <a href="/portal" className="text-xs uppercase tracking-[0.15em] text-neutral-500 hover:text-black">My Account</a>
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <CartSheet />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t px-6 py-8">
          <nav className="flex flex-col gap-6">
            {["Services", "Portfolio", "Book Now", "Shop"].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(" ", "")}`} onClick={() => setMobileMenuOpen(false)} className="text-sm uppercase tracking-widest">{item}</a>
            ))}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-sm uppercase tracking-widest flex items-center gap-2"><Instagram className="w-4 h-4" /> Instagram</a>
            <a href="/portal" className="text-sm uppercase tracking-widest text-neutral-500">My Account</a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero with AI Box
const Hero = () => {
  const [aiInput, setAiInput] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [sessionId] = useState(() => `hero-${Date.now()}`);

  const handleAiSubmit = async () => {
    if (!aiInput.trim() || aiLoading) return;
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, { session_id: sessionId, message: aiInput });
      setAiResponse(response.data.response);
      setAiInput("");
    } catch {
      setAiResponse("Sorry, I couldn't process that. Contact us directly!");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <section className="min-h-screen flex flex-col justify-center items-center text-center px-6 pt-28">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] font-medium mb-6 text-neutral-500 animate-fade-in-up">Petersburg, VA • RVA • Hopewell</p>
        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-tight mb-6 animate-fade-in-up animation-delay-200">
          Capturing Your<br /><span className="italic">Moments</span>
        </h1>
        <p className="text-base md:text-lg font-light text-neutral-600 max-w-xl mx-auto mb-8 animate-fade-in-up animation-delay-400">
          Event photography, sports media days, senior portraits, and creative editorial sessions.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10 animate-fade-in-up animation-delay-600">
          <a href="#booking"><Button className="bg-black text-white hover:bg-neutral-800 rounded-none px-8 py-6 text-sm uppercase tracking-widest">Book a Session</Button></a>
          <a href="#services"><Button variant="outline" className="border-black hover:bg-black hover:text-white rounded-none px-8 py-6 text-sm uppercase tracking-widest">View Services</Button></a>
        </div>

        <div className="max-w-xl mx-auto animate-fade-in-up animation-delay-600">
          <div className="bg-neutral-50 border border-neutral-200 p-6">
            <div className="flex items-center gap-2 mb-4 justify-center">
              <Sparkles className="w-5 h-5 text-neutral-600" />
              <span className="text-sm font-medium uppercase tracking-widest text-neutral-600">AI Creative Assistant</span>
            </div>
            <p className="text-sm text-neutral-500 mb-4">Need inspiration? Ask about outfit ideas, poses, locations, or pricing info.</p>
            <div className="flex gap-2">
              <Input value={aiInput} onChange={e => setAiInput(e.target.value)} onKeyPress={e => e.key === "Enter" && handleAiSubmit()} placeholder="Ask me anything..." className="flex-1 rounded-none" />
              <Button onClick={handleAiSubmit} disabled={aiLoading} className="bg-black text-white hover:bg-neutral-800 rounded-none px-4">
                {aiLoading ? "..." : <Send className="w-4 h-4" />}
              </Button>
            </div>
            {aiResponse && <div className="mt-4 p-4 bg-white border text-left text-sm"><pre className="whitespace-pre-wrap font-sans">{aiResponse}</pre></div>}
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServiceCard = ({ service, idx }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-neutral-200">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-6 flex items-center justify-between text-left hover:bg-neutral-50">
        <div className="flex items-center gap-3"><Camera className="w-5 h-5" /><h3 className="font-serif text-lg md:text-xl">{service.title}</h3></div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {expanded && (
        <div className="px-6 pb-6 border-t border-neutral-100">
          <div className="pt-4 space-y-3">
            {service.packages.map((pkg, i) => (
              <div key={i} className="flex justify-between items-start py-2 border-b border-neutral-100 last:border-0">
                <div className="flex-1"><span className="font-medium">{pkg.name}</span><p className="text-sm text-neutral-500">{pkg.details}</p></div>
                <span className="text-lg font-serif ml-4">{pkg.price}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Services = () => {
  const [availability, setAvailability] = useState({ 
    march_special: { remaining: 4, is_available: true },
    limited_graduation: { remaining: 5, is_available: true }
  });
  
  useEffect(() => {
    axios.get(`${API}/limited-time-availability`).then(res => setAvailability(res.data)).catch(() => {});
  }, []);

  const specialServices = [
    { 
      title: "🔥 March Monthly Special", 
      description: "First 4 bookings only this month! Perfect for any occasion.",
      packages: [{ name: "Session", price: "$100", details: "1-hour session, 8 edited images. Additional edits are add-ons." }],
      addons: ["Additional edited images – $15 each", "Extra time – $50/30min"],
      isLimited: true,
      packageKey: "march-special",
      remaining: availability.march_special?.remaining ?? 4,
      isAvailable: availability.march_special?.is_available ?? true,
      highlight: true
    },
    { 
      title: "🎓 Limited Time Graduation Offer", 
      description: "First 5 graduation photo bookings only! Celebrate your achievement.",
      packages: [{ name: "Session", price: "$75", details: "1-hour session, 5 edited images" }],
      addons: ["Additional edited images – $15 each", "Extra time – $50/30min"],
      isLimited: true,
      packageKey: "limited-graduation",
      remaining: availability.limited_graduation?.remaining ?? 5,
      isAvailable: availability.limited_graduation?.is_available ?? true,
      highlight: true
    },
    { 
      title: "Quick Flicks", 
      description: "Need quick photos without edits? Perfect for social media content, headshots, or casual shots.",
      packages: [{ name: "Per Photo", price: "$15", details: "No edits included - raw, high-quality photos" }],
      addons: ["Add editing – $15 per photo"],
      highlight: true
    }
  ];

  const services = [
    { 
      title: "Just Because Shoot", 
      description: "Perfect for capturing yourself in the moment — no birthday, no milestone, no special occasion required. Whether you're feeling confident, updating your look, celebrating growth, or just want fresh content for social media.",
      packages: [{ name: "Session", price: "$175", details: "45-min session, 1 location, 1 outfit, 8 edited images" }],
      addons: ["Additional images – $15 each", "Extra outfit – $25", "Extended time – $50/30min"]
    },
    { 
      title: "Graduation Shoot", 
      description: "Celebrate hard work, late nights, growth, and achievement. Classic cap-and-gown portraits, fun campus shots, or stylish celebratory vibes.",
      packages: [{ name: "Session", price: "$250", details: "1-hour session, 1 location, 2 outfits (cap & gown + 1), 12 edited images" }],
      addons: ["Additional images – $15 each", "Extra outfit – $25", "Extended time – $50/30min", "Announcement design – $75"]
    },
    { 
      title: "Wedding Photography", 
      description: "From getting ready to the final send-off — authentic emotions, candid memories, and timeless portraits that tell your love story.",
      packages: [{ name: "Starting at", price: "$1,500", details: "6 hours coverage, consultation, 200+ edited images, print release" }],
      addons: ["Engagement session – $300", "Additional hour – $200", "Second photographer – $400", "Album – Starting at $350"]
    },
    { 
      title: "Family Portraits", 
      description: "Relaxed and natural sessions focused on genuine smiles, laughter, and meaningful interactions.",
      packages: [{ name: "Session", price: "$300", details: "1-hour session, 1 location, up to 5 members, 15 edited images" }],
      addons: ["Additional family member – $15 each", "Additional images – $15 each", "Extended time – $50/30min"]
    },
    { 
      title: "Event Photography", 
      packages: [{ name: "Basic", price: "$250", details: "2 hours, 300+ photos" }, { name: "Premium", price: "$300", details: "2 hours, 500+ photos, 25 edits" }] 
    },
    { 
      title: "Sports & Gameday", 
      packages: [{ name: "Team Media Day", price: "$300", details: "Team & individual portraits" }, { name: "Gameday Package", price: "$100-800", details: "15-150 images" }, { name: "Individual", price: "From $10", details: "Per photo" }] 
    }
  ];

  const renderServiceCard = (service, idx, isSpecial = false) => (
    <Dialog key={idx}>
      <DialogTrigger asChild>
        <div className={`border p-6 cursor-pointer transition-colors ${isSpecial ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300 hover:border-amber-500' : 'bg-white border-neutral-200 hover:border-black'}`}>
          {service.isLimited && (
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-xs font-bold px-2 py-1 rounded ${service.isAvailable ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                {service.isAvailable ? `${service.remaining} SPOTS LEFT` : 'SOLD OUT'}
              </span>
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
          )}
          <h3 className="font-serif text-xl mb-2">{service.title}</h3>
          {service.description && <p className="text-sm text-neutral-500 mb-3 line-clamp-2">{service.description}</p>}
          <div className="flex items-center justify-between">
            <span className={`text-2xl font-serif ${isSpecial ? 'text-amber-700' : ''}`}>{service.packages[0].price}</span>
            <span className="text-xs uppercase tracking-widest text-neutral-400">View Details</span>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{service.title}</DialogTitle>
        </DialogHeader>
        {service.isLimited && (
          <div className={`p-3 rounded mb-4 ${service.isAvailable ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm font-medium ${service.isAvailable ? 'text-green-700' : 'text-red-700'}`}>
              {service.isAvailable ? `🔥 ${service.remaining} spots remaining this month!` : '❌ All spots taken for this month. Check back next month!'}
            </p>
          </div>
        )}
        {service.description && <p className="text-neutral-600 mb-4">{service.description}</p>}
        <div className="space-y-4">
          {service.packages.map((pkg, i) => (
            <div key={i} className="bg-neutral-50 p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">{pkg.name}</span>
                <span className="text-xl font-serif">{pkg.price}</span>
              </div>
              <p className="text-sm text-neutral-500">{pkg.details}</p>
            </div>
          ))}
          {service.addons && (
            <div className="border-t pt-4">
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Optional Add-Ons</p>
              <ul className="text-sm text-neutral-600 space-y-1">
                {service.addons.map((addon, i) => <li key={i}>• {addon}</li>)}
              </ul>
            </div>
          )}
        </div>
        <div className="mt-4">
          <a href="#booking" className={`block w-full text-center py-3 text-sm uppercase tracking-widest ${service.isLimited && !service.isAvailable ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed' : 'bg-black text-white hover:bg-neutral-800'}`}>
            {service.isLimited && !service.isAvailable ? 'Sold Out This Month' : 'Book This Session'}
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <section id="services" className="py-20 md:py-28 px-6 md:px-12 bg-neutral-50">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] font-medium mb-4 text-neutral-500">What I Offer</p>
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight">Services & Pricing</h2>
          <p className="text-neutral-500 mt-4">Click on a service to see pricing details</p>
        </div>
        
        {/* Special Offers Section */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <span>Special Offers</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {specialServices.map((service, idx) => renderServiceCard(service, idx, true))}
          </div>
        </div>
        
        {/* Regular Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, idx) => renderServiceCard(service, idx, false))}
        </div>
        <div className="mt-8 bg-white p-6 border border-neutral-200">
          <p className="text-sm"><span className="font-medium">25% Security Deposit Required</span><span className="text-neutral-500 ml-2">• Non-refundable • Charged after admin approval</span></p>
        </div>
      </div>
    </section>
  );
};

// Professional Masonry Portfolio with Categories and Lightbox
const Portfolio = () => {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const categories = ["all", "sports", "portraits", "editorial", "events", "weddings"];
  
  // Professional sample images with varied aspect ratios for masonry
  const defaultImages = [
    { id: "1", url: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800&q=80", category: "editorial", aspect: "tall" },
    { id: "2", url: "https://images.unsplash.com/photo-1552374196-c4e7ffc6e126?w=800&q=80", category: "portraits", aspect: "square" },
    { id: "3", url: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800&q=80", category: "editorial", aspect: "tall" },
    { id: "4", url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80", category: "portraits", aspect: "square" },
    { id: "5", url: "https://images.unsplash.com/photo-1546961342-ea5f71b193f3?w=800&q=80", category: "sports", aspect: "wide" },
    { id: "6", url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80", category: "weddings", aspect: "tall" },
    { id: "7", url: "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800&q=80", category: "events", aspect: "square" },
    { id: "8", url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&q=80", category: "portraits", aspect: "tall" },
    { id: "9", url: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=800&q=80", category: "editorial", aspect: "square" },
    { id: "10", url: "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800&q=80", category: "portraits", aspect: "tall" },
    { id: "11", url: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80", category: "sports", aspect: "wide" },
    { id: "12", url: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&q=80", category: "weddings", aspect: "square" }
  ];

  useEffect(() => {
    setIsLoading(true);
    axios.get(`${API}/portfolio`).then(res => {
      const imgs = res.data.length > 0 
        ? res.data.map((img, i) => ({ 
            ...img, 
            url: `${BACKEND_URL}/api/uploads/${img.filename}?w=800&q=85`, 
            fullUrl: `${BACKEND_URL}/api/uploads/${img.filename}`, 
            category: img.category || "editorial",
            aspect: ["tall", "square", "wide"][i % 3]
          })) 
        : defaultImages;
      setImages(imgs);
      setFilteredImages(imgs);
      setIsLoading(false);
    }).catch(() => {
      setImages(defaultImages);
      setFilteredImages(defaultImages);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredImages(images);
    } else {
      setFilteredImages(images.filter(img => img.category?.toLowerCase() === activeCategory));
    }
  }, [activeCategory, images]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const navigateLightbox = (direction) => {
    setLightboxIndex(prev => {
      const newIndex = prev + direction;
      if (newIndex < 0) return filteredImages.length - 1;
      if (newIndex >= filteredImages.length) return 0;
      return newIndex;
    });
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "ArrowLeft") navigateLightbox(-1);
      if (e.key === "ArrowRight") navigateLightbox(1);
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, filteredImages.length]);

  const getAspectClass = (aspect, index) => {
    // Create varied masonry layout
    if (index === 0) return "md:col-span-2 md:row-span-2";
    if (aspect === "tall") return "row-span-2";
    if (aspect === "wide") return "md:col-span-2";
    return "";
  };

  return (
    <section id="portfolio" className="py-20 md:py-32 px-6 md:px-12 bg-neutral-50">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-medium mb-4 text-neutral-400">Selected Work</p>
            <h2 className="font-serif text-4xl md:text-6xl font-normal tracking-tight">Portfolio</h2>
            <p className="text-neutral-500 mt-4 max-w-lg">Capturing authentic moments, emotions, and stories through the lens. Each image tells a unique narrative.</p>
          </div>
          
          {/* Category Filter Pills */}
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 text-xs uppercase tracking-widest font-medium transition-all duration-300 ${
                  activeCategory === cat 
                    ? "bg-black text-white shadow-lg" 
                    : "bg-white text-neutral-600 hover:bg-black hover:text-white border border-neutral-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        
        {/* Masonry Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {filteredImages.map((img, i) => (
              <div 
                key={img.id || i} 
                onClick={() => openLightbox(i)} 
                className="group cursor-pointer overflow-hidden break-inside-avoid mb-4 relative"
              >
                <div className="relative overflow-hidden bg-neutral-200">
                  <img 
                    src={img.url} 
                    alt={`${img.category} photography`}
                    className="w-full h-auto object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110" 
                    loading="lazy"
                    decoding="async"
                    onError={(e) => e.target.src = defaultImages[0].url}
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        <div className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center mb-3">
                          <ZoomIn className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <span className="text-white text-xs uppercase tracking-widest font-medium">{img.category}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {filteredImages.length === 0 && !isLoading && (
          <div className="text-center py-20">
            <Camera className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
            <p className="text-neutral-500 text-lg">No images in this category yet</p>
            <p className="text-neutral-400 text-sm mt-2">Check back soon for new content!</p>
          </div>
        )}
        
        {/* Instagram CTA */}
        <div className="mt-16 text-center">
          <a 
            href={INSTAGRAM_URL} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center gap-4 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <Instagram className="w-5 h-5" /> 
            Follow on Instagram 
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
      
      {/* Professional Lightbox Modal */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] w-full h-[95vh] p-0 bg-black/95 border-none backdrop-blur-sm">
          <DialogHeader className="sr-only">
            <DialogTitle>Photo Gallery - {filteredImages[lightboxIndex]?.category}</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-full flex items-center justify-center">
            {filteredImages[lightboxIndex] && (
              <>
                <img 
                  src={filteredImages[lightboxIndex].fullUrl || filteredImages[lightboxIndex].url} 
                  alt={`${filteredImages[lightboxIndex].category} photography`}
                  className="max-w-full max-h-[85vh] object-contain shadow-2xl" 
                />
                
                {/* Image Info */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
                  <span className="text-white/80 text-sm uppercase tracking-widest bg-black/50 px-4 py-2 backdrop-blur-sm">
                    {filteredImages[lightboxIndex].category} • {lightboxIndex + 1} / {filteredImages.length}
                  </span>
                </div>
              </>
            )}
            
            {/* Close Button */}
            <button 
              onClick={() => setLightboxOpen(false)} 
              className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Navigation */}
            <button 
              onClick={() => navigateLightbox(-1)} 
              className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all"
            >
              <ChevronDown className="w-8 h-8 rotate-90" />
            </button>
            <button 
              onClick={() => navigateLightbox(1)} 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all"
            >
              <ChevronDown className="w-8 h-8 -rotate-90" />
            </button>
            
            {/* Thumbnail Strip */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto py-2 px-4">
              {filteredImages.slice(Math.max(0, lightboxIndex - 3), lightboxIndex + 4).map((img, i) => {
                const actualIndex = Math.max(0, lightboxIndex - 3) + i;
                return (
                  <button
                    key={img.id}
                    onClick={() => setLightboxIndex(actualIndex)}
                    className={`w-16 h-16 flex-shrink-0 overflow-hidden transition-all ${actualIndex === lightboxIndex ? "ring-2 ring-white scale-110" : "opacity-50 hover:opacity-80"}`}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Card Input Component for Booking
const CardInputForm = ({ bookingId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get setup intent
      const setupRes = await axios.post(`${API}/booking/setup-intent?booking_id=${bookingId}`);
      const { client_secret } = setupRes.data;
      
      // Confirm card setup
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card: elements.getElement(CardElement) }
      });
      
      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }
      
      // Save card to booking
      await axios.post(`${API}/booking/save-card`, {
        booking_id: bookingId,
        payment_method_id: setupIntent.payment_method
      });
      
      toast.success("Card saved! Awaiting admin confirmation.");
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save card");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-neutral-200 rounded bg-white">
        <CardElement options={{
          style: {
            base: { fontSize: "16px", color: "#000", "::placeholder": { color: "#9ca3af" } },
            invalid: { color: "#ef4444" }
          }
        }} />
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <p className="text-xs text-neutral-500">Your card will be saved securely. You will only be charged after admin confirms your booking.</p>
      <Button type="submit" disabled={!stripe || loading} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none py-6">
        {loading ? "Saving..." : "Save Card & Complete Booking"}
      </Button>
    </form>
  );
};

// Booking Section with Card on File
const Booking = () => {
  const [packages, setPackages] = useState({});
  const [availability, setAvailability] = useState({ blocked_dates: [], booked_dates: [] });
  const [estimate, setEstimate] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [terms, setTerms] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", date: null, session_type: "", package: "", budget: "", location: "", distance_miles: "", same_day_delivery: false, message: "", agreed_to_terms: false });

  useEffect(() => {
    axios.get(`${API}/packages`).then(res => setPackages(res.data)).catch(() => {});
    axios.get(`${API}/availability`).then(res => setAvailability(res.data)).catch(() => {});
    axios.get(`${API}/terms`).then(res => setTerms(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (formData.package) {
      axios.post(`${API}/booking/estimate`, { ...formData, date: formData.date ? format(formData.date, "yyyy-MM-dd") : null, budget: formData.budget ? parseFloat(formData.budget) : null, distance_miles: formData.distance_miles ? parseFloat(formData.distance_miles) : null }).then(res => setEstimate(res.data)).catch(() => {});
    }
  }, [formData.package, formData.same_day_delivery, formData.distance_miles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.date || !formData.session_type || !formData.package || !formData.location || !formData.agreed_to_terms) {
      toast.error("Please fill all required fields and agree to terms");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...formData, date: format(formData.date, "yyyy-MM-dd"), budget: formData.budget ? parseFloat(formData.budget) : null, distance_miles: formData.distance_miles ? parseFloat(formData.distance_miles) : null });
      setBookingComplete(true);
      toast.success("Booking request submitted!");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const isDateUnavailable = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return availability.blocked_dates.includes(dateStr) || availability.booked_dates.includes(dateStr) || date < new Date();
  };

  const sessionTypes = [
    { value: "march-special", label: "🔥 March Monthly Special ($100)" },
    { value: "limited-graduation", label: "🎓 Limited Graduation Offer ($75)" },
    { value: "quick-flicks", label: "Quick Flicks ($15/photo)" },
    { value: "just-because", label: "Just Because Shoot" },
    { value: "graduation", label: "Graduation" },
    { value: "wedding", label: "Wedding" },
    { value: "family", label: "Family Portraits" },
    { value: "event", label: "Event Photography" },
    { value: "sports", label: "Sports & Gameday" }
  ];
  
  const getPackageOptions = () => ({
    "march-special": ["march-special"],
    "limited-graduation": ["limited-graduation"],
    "quick-flicks": ["quick-flicks"],
    "just-because": ["just-because"],
    "graduation": ["graduation"],
    "wedding": ["wedding-basic"],
    "family": ["family-portrait"],
    "event": ["event-basic", "event-premium"],
    "sports": ["team-media-day", "sports-media-special", "sports-media-1hr", "gameday-basic", "gameday-team", "gameday-individual"]
  }[formData.session_type] || []);

  return (
    <section id="booking" className="py-20 md:py-28 px-6 md:px-12 bg-neutral-50">
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-medium mb-4 text-neutral-500">Schedule Your Session</p>
            <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight mb-6">Book Now</h2>
            <p className="text-neutral-600 mb-4">Submit your booking request. Once approved, you'll receive a payment link for the 25% deposit.</p>
            <div className="bg-blue-50 border border-blue-200 p-4 mb-6 text-sm">
              <p className="font-medium flex items-center gap-2"><Clock className="w-4 h-4" /> How It Works:</p>
              <ol className="mt-2 space-y-1 text-blue-800 list-decimal list-inside">
                <li>Submit your booking request</li>
                <li>Admin reviews and approves your date</li>
                <li>Receive payment link for 25% deposit</li>
                <li>Booking confirmed after payment</li>
              </ol>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 p-4 mb-6 text-sm">
              <p className="font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Important Policies:</p>
              <ul className="mt-2 space-y-1 text-yellow-800">
                <li>• All payments are <strong>non-refundable</strong></li>
                <li>• No-shows charged <strong>50% of total</strong></li>
                <li>• Delivery: <strong>5-7 business days</strong></li>
              </ul>
            </div>
            <div className="bg-white p-6 border border-neutral-200 mb-6">
              <h4 className="font-medium mb-4 flex items-center gap-2"><Calendar className="w-4 h-4" /> Availability</h4>
              <CalendarComponent mode="single" selected={formData.date} onSelect={date => setFormData({ ...formData, date })} disabled={isDateUnavailable} className="rounded-md border-0" />
            </div>
            <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
              <DialogTrigger asChild><button className="text-sm underline text-neutral-600 hover:text-black flex items-center gap-2"><FileText className="w-4 h-4" /> View Terms of Service</button></DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh]"><DialogHeader><DialogTitle className="font-serif text-xl">Terms of Service & Agreement</DialogTitle></DialogHeader><ScrollArea className="h-[60vh] pr-4">{terms && <pre className="whitespace-pre-wrap font-sans text-sm">{terms.terms_of_service}</pre>}</ScrollArea></DialogContent>
            </Dialog>
          </div>

          <div>
            {bookingComplete ? (
              <div className="bg-white p-8 border border-neutral-200 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500 text-white flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8" /></div>
                <h3 className="font-serif text-2xl mb-4">Booking Request Submitted!</h3>
                <p className="text-neutral-600 mb-4">We'll review your request and send you a payment link once approved.</p>
                <div className="bg-neutral-50 p-4 mt-6 text-left">
                  <p className="text-sm mb-2"><strong>What's Next:</strong></p>
                  <ul className="text-sm text-neutral-600 space-y-1">
                    <li>• Check your email for confirmation</li>
                    <li>• Wait for admin approval (usually within 24 hours)</li>
                    <li>• Complete payment when you receive the link</li>
                  </ul>
                </div>
                {estimate && <div className="bg-neutral-50 p-4 mt-4 text-left"><p className="text-sm mb-2"><strong>Total Estimate:</strong> ${estimate.total_estimate}</p><p className="text-sm"><strong>Deposit (25%):</strong> ${estimate.deposit_required}</p></div>}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 border border-neutral-200">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Name *</label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="rounded-none" required /></div>
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Email *</label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="rounded-none" required /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Phone</label><Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="rounded-none" /></div>
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Date *</label>
                    <Popover><PopoverTrigger asChild><Button variant="outline" className="w-full rounded-none justify-start font-normal"><Calendar className="mr-2 h-4 w-4" />{formData.date ? format(formData.date, "PPP") : "Pick date"}</Button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><CalendarComponent mode="single" selected={formData.date} onSelect={date => setFormData({ ...formData, date })} disabled={isDateUnavailable} /></PopoverContent></Popover>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Session Type *</label>
                    <Select value={formData.session_type} onValueChange={v => setFormData({ ...formData, session_type: v, package: "" })}><SelectTrigger className="rounded-none"><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent>{sessionTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Package *</label>
                    <Select value={formData.package} onValueChange={v => setFormData({ ...formData, package: v })} disabled={!formData.session_type}><SelectTrigger className="rounded-none"><SelectValue placeholder="Select package" /></SelectTrigger><SelectContent>{getPackageOptions().map(p => <SelectItem key={p} value={p}>{packages[p]?.name || p}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Location *</label><Input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} className="rounded-none" placeholder="Session location" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Distance (miles)</label><Input type="number" value={formData.distance_miles} onChange={e => setFormData({ ...formData, distance_miles: e.target.value })} className="rounded-none" placeholder="From Petersburg" /></div>
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Budget (optional)</label><Input type="number" value={formData.budget} onChange={e => setFormData({ ...formData, budget: e.target.value })} className="rounded-none" placeholder="$" /></div>
                </div>
                <div className="flex items-center gap-3"><Checkbox checked={formData.same_day_delivery} onCheckedChange={c => setFormData({ ...formData, same_day_delivery: c })} /><label className="text-sm">Same Day Delivery (+$50-75)</label></div>
                <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Message</label><Textarea value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} className="rounded-none min-h-[80px]" placeholder="Tell us about your vision..." /></div>
                {estimate && <div className="bg-neutral-50 p-4 border"><div className="flex justify-between font-medium"><span>Total Estimate</span><span>${estimate.total_estimate}</span></div><div className="flex justify-between text-sm mt-2"><span>Deposit (25%)</span><span className="font-serif text-lg">${estimate.deposit_required}</span></div><p className="text-xs text-neutral-500 mt-2">*Deposit charged after admin approval</p></div>}
                <div className="flex items-start gap-3"><Checkbox checked={formData.agreed_to_terms} onCheckedChange={c => setFormData({ ...formData, agreed_to_terms: c })} /><label className="text-sm">I agree to the <button type="button" onClick={() => setTermsOpen(true)} className="underline">Terms of Service</button> including no-refund and no-show policies *</label></div>
                <Button type="submit" disabled={loading} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none px-8 py-6 text-sm uppercase tracking-widest">{loading ? "Submitting..." : "Submit Booking Request"}</Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Shop Section with Albums
const Shop = () => {
  const { addItem } = useCart();
  const [activeTab, setActiveTab] = useState("prints");
  const [products, setProducts] = useState({ prints: [], digital: [] });
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [albumRequiresPassword, setAlbumRequiresPassword] = useState(false);
  const [albumPasswordInput, setAlbumPasswordInput] = useState("");
  const [photoDialog, setPhotoDialog] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [printSize, setPrintSize] = useState("print-4x6");
  const [addFrame, setAddFrame] = useState(false);
  const [printFrameStates, setPrintFrameStates] = useState({});

  useEffect(() => {
    axios.get(`${API}/products?category=print`).then(res => setProducts(p => ({ ...p, prints: res.data.filter(x => x.category === "print") }))).catch(() => {});
    axios.get(`${API}/products?category=digital`).then(res => setProducts(p => ({ ...p, digital: res.data }))).catch(() => {});
    axios.get(`${API}/albums`).then(res => setAlbums(res.data)).catch(() => {});
    
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "success") { toast.success("Payment successful! Check your email."); window.history.replaceState({}, "", window.location.pathname); }
    if (params.get("status") === "cancelled") { toast.error("Payment cancelled"); window.history.replaceState({}, "", window.location.pathname); }
  }, []);

  const openAlbum = async (album, password = null) => {
    setSelectedAlbum(album);
    setAlbumPasswordInput("");
    try {
      const res = await axios.get(`${API}/albums/${album.id}${password ? `?password=${encodeURIComponent(password)}` : ""}`);
      if (res.data.requires_password) {
        setAlbumRequiresPassword(true);
        setAlbumPhotos([]);
      } else {
        setAlbumRequiresPassword(false);
        setAlbumPhotos(res.data.photos);
      }
    } catch { setAlbumPhotos([]); }
  };

  const submitAlbumPassword = async () => {
    if (!albumPasswordInput) return toast.error("Enter password");
    await openAlbum(selectedAlbum, albumPasswordInput);
    if (albumRequiresPassword) {
      toast.error("Incorrect password");
    }
  };

  const togglePrintFrame = (productId) => {
    setPrintFrameStates(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  const addProductToCart = (product, qty = 1, frame = false) => {
    addItem({ id: product.id, type: "product", name: product.name, price: product.price, image: product.image_url, add_frame: frame && product.category === "print", print_size: product.size });
  };

  const addPhotoToCart = (photo, type, size = null, frame = false) => {
    const prices = { digital: 10, "print-4x6": 10, "print-5x7": 15, "print-8x10": 25, "print-11x14": 50 };
    addItem({
      id: `${photo.id}-${type}-${size || "digital"}`,
      type: type === "digital" ? "album_photo_digital" : "album_photo_print",
      name: `${type === "digital" ? "Digital" : size?.replace("print-", "")} Photo`,
      price: prices[size] || prices.digital,
      image: `${API}/albums/photo/${photo.id}/watermarked`,
      photo_id: photo.id,
      album_id: photo.album_id,
      print_size: size,
      add_frame: frame
    });
    setPhotoDialog(null);
  };

  return (
    <section id="shop" className="py-20 md:py-28 px-6 md:px-12">
      <div className="max-w-screen-xl mx-auto">
        <div className="mb-12"><p className="text-xs uppercase tracking-[0.3em] font-medium mb-4 text-neutral-500">Collect</p><h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight">Shop</h2></div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-xl grid-cols-3 mb-8 rounded-none bg-neutral-100">
            <TabsTrigger value="prints" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white"><Printer className="w-4 h-4 mr-2" /> Prints</TabsTrigger>
            <TabsTrigger value="digital" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white"><Download className="w-4 h-4 mr-2" /> Digital</TabsTrigger>
            <TabsTrigger value="albums" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white"><FolderOpen className="w-4 h-4 mr-2" /> Albums</TabsTrigger>
          </TabsList>

          <TabsContent value="prints">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {products.prints.map(product => (
                <div key={product.id} className="border border-neutral-200 bg-white">
                  <div className="aspect-square bg-neutral-100 relative"><img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />{product.size && <span className="absolute top-3 left-3 bg-black text-white text-xs px-2 py-1">{product.size}</span>}</div>
                  <div className="p-4">
                    <h4 className="font-medium mb-1">{product.name}</h4>
                    <p className="text-sm text-neutral-500 mb-2">{product.description}</p>
                    <div className="flex items-center gap-2 mb-3">
                      <Checkbox 
                        checked={printFrameStates[product.id] || false} 
                        onCheckedChange={() => togglePrintFrame(product.id)} 
                        id={`frame-${product.id}`}
                      />
                      <label htmlFor={`frame-${product.id}`} className="text-xs flex items-center gap-1">
                        <Frame className="w-3 h-3" /> Matt Board (+$5)
                      </label>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-serif text-xl">${(product.price + (printFrameStates[product.id] ? 5 : 0)).toFixed(2)}</span>
                      <Button onClick={() => addProductToCart(product, 1, printFrameStates[product.id])} className="bg-black text-white hover:bg-neutral-800 rounded-none px-4 py-2 text-xs uppercase tracking-widest">Add</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="digital">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {products.digital.map(product => (
                <div key={product.id} className="border border-neutral-200 bg-white">
                  <div className="aspect-square bg-neutral-100"><img src={product.image_url} alt={product.name} className="w-full h-full object-cover" /></div>
                  <div className="p-4">
                    <h4 className="font-medium mb-1">{product.name}</h4>
                    <p className="text-sm text-neutral-500 mb-3">{product.description}</p>
                    <div className="flex justify-between items-center"><span className="font-serif text-xl">${product.price}</span><Button onClick={() => addProductToCart(product)} className="bg-black text-white hover:bg-neutral-800 rounded-none px-4 py-2 text-xs uppercase tracking-widest">Add</Button></div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="albums">
            {selectedAlbum ? (
              <div>
                <button onClick={() => setSelectedAlbum(null)} className="flex items-center gap-2 text-sm mb-6 hover:underline"><ArrowRight className="w-4 h-4 rotate-180" /> Back to Albums</button>
                <h3 className="font-serif text-2xl mb-4">{selectedAlbum.title}</h3>
                {selectedAlbum.description && <p className="text-neutral-600 mb-6">{selectedAlbum.description}</p>}
                <p className="text-sm text-neutral-500 mb-6">Click on a photo to purchase as digital ($10) or print ($10-50)</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {albumPhotos.map(photo => (
                    <div key={photo.id} className="relative group cursor-pointer" onClick={() => setPhotoDialog(photo)}>
                      <div className="aspect-square bg-neutral-100 overflow-hidden">
                        <img src={`${API}/albums/photo/${photo.id}/watermarked`} alt="Album photo" className="w-full h-full object-cover" />
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button className="bg-white text-black hover:bg-neutral-100 rounded-none"><ShoppingCart className="w-4 h-4 mr-2" /> Buy</Button>
                      </div>
                    </div>
                  ))}
                </div>
                {albumPhotos.length === 0 && <p className="text-neutral-500 text-center py-12">No photos in this album yet</p>}
              </div>
            ) : (
              <div>
                <p className="text-neutral-600 mb-6">Browse photo albums and purchase individual photos as digital downloads or prints.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {albums.map(album => (
                    <div key={album.id} className="border border-neutral-200 bg-white cursor-pointer hover:border-black transition-colors" onClick={() => openAlbum(album)}>
                      <div className="aspect-[4/3] bg-neutral-100 overflow-hidden relative">
                        {album.cover_filename ? <img src={`${API}/albums/${album.id}/cover`} alt={album.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><FolderOpen className="w-12 h-12 text-neutral-300" /></div>}
                        {album.password && <div className="absolute top-2 right-2 bg-black/70 text-white p-1 rounded"><Lock className="w-4 h-4" /></div>}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2">
                          <h4 className="font-serif text-lg">{album.title}</h4>
                          {album.password && <Lock className="w-4 h-4 text-neutral-400" />}
                        </div>
                        <p className="text-sm text-neutral-500">{album.photo_count} photos</p>
                      </div>
                    </div>
                  ))}
                </div>
                {albums.length === 0 && <p className="text-neutral-500 text-center py-12">No albums available yet</p>}
              </div>
            )}
            
            {/* Password prompt for protected albums */}
            {selectedAlbum && albumRequiresPassword && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setSelectedAlbum(null); setAlbumRequiresPassword(false); }}>
                <div className="bg-white p-8 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                  <div className="text-center mb-6">
                    <Lock className="w-12 h-12 mx-auto mb-4 text-neutral-400" />
                    <h3 className="font-serif text-2xl mb-2">{selectedAlbum.title}</h3>
                    <p className="text-neutral-600">This album is password protected. Enter the password to view photos.</p>
                  </div>
                  <Input type="password" placeholder="Enter album password" value={albumPasswordInput} onChange={e => setAlbumPasswordInput(e.target.value)} className="rounded-none mb-4" onKeyPress={e => e.key === "Enter" && submitAlbumPassword()} />
                  <div className="flex gap-3">
                    <Button onClick={() => { setSelectedAlbum(null); setAlbumRequiresPassword(false); }} variant="outline" className="flex-1 rounded-none">Cancel</Button>
                    <Button onClick={submitAlbumPassword} className="flex-1 bg-black text-white hover:bg-neutral-800 rounded-none">Unlock Album</Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Photo Zoom Lightbox */}
        <Dialog open={!!lightboxPhoto} onOpenChange={() => setLightboxPhoto(null)}>
          <DialogContent className="max-w-4xl w-full h-[80vh] p-0 bg-black border-none">
            <div className="relative w-full h-full flex items-center justify-center">
              {lightboxPhoto && <img src={`${API}/albums/photo/${lightboxPhoto.id}/watermarked`} alt="Photo" className="max-w-full max-h-full object-contain" />}
              <button onClick={() => setLightboxPhoto(null)} className="absolute top-4 right-4 text-white hover:text-neutral-300"><X className="w-8 h-8" /></button>
              <Button onClick={() => setPhotoDialog(lightboxPhoto)} className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white text-black hover:bg-neutral-100 rounded-none">
                <ShoppingCart className="w-4 h-4 mr-2" /> Purchase This Photo
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo Purchase Dialog */}
        <Dialog open={!!photoDialog} onOpenChange={() => setPhotoDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-xl">Purchase Photo</DialogTitle></DialogHeader>
            {photoDialog && (
              <div className="space-y-6">
                <div className="aspect-square bg-neutral-100 overflow-hidden max-h-64 mx-auto cursor-zoom-in" onClick={() => { setLightboxPhoto(photoDialog); setPhotoDialog(null); }}>
                  <img src={`${API}/albums/photo/${photoDialog.id}/watermarked`} alt="Photo" className="w-full h-full object-contain" />
                </div>
                <p className="text-sm text-neutral-500 text-center">Click image to zoom • Watermark removed after purchase</p>
                
                <div className="space-y-4">
                  <Button onClick={() => addPhotoToCart(photoDialog, "digital")} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none py-4">
                    <Download className="w-4 h-4 mr-2" /> Digital Download - $10
                  </Button>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-3">Or purchase as print:</p>
                    <Select value={printSize} onValueChange={setPrintSize}>
                      <SelectTrigger className="rounded-none mb-3"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="print-4x6">4x6 Print - $10</SelectItem>
                        <SelectItem value="print-5x7">5x7 Print - $15</SelectItem>
                        <SelectItem value="print-8x10">8x10 Print - $25</SelectItem>
                        <SelectItem value="print-11x14">11x14 Print - $50</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2 mb-3">
                      <Checkbox checked={addFrame} onCheckedChange={setAddFrame} />
                      <label className="text-sm"><Frame className="w-3 h-3 inline" /> Add Matt Board (+$5)</label>
                    </div>
                    <Button onClick={() => addPhotoToCart(photoDialog, "print", printSize, addFrame)} variant="outline" className="w-full rounded-none py-4 border-black">
                      <Printer className="w-4 h-4 mr-2" /> Add Print to Cart
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="mt-12 p-6 bg-black text-white text-center">
          <p className="text-xs uppercase tracking-widest mb-3 text-neutral-400">Alternative Payment</p>
          <div className="flex items-center justify-center gap-3 mb-2"><DollarSign className="w-6 h-6" /><span className="font-serif text-2xl">{CASHAPP_TAG}</span></div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => (
  <section id="contact" className="py-20 md:py-28 px-6 md:px-12 bg-neutral-50">
    <div className="max-w-screen-xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-medium mb-4 text-neutral-500">Get In Touch</p>
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight mb-8">Contact</h2>
          <div className="space-y-4">
            {[
              { icon: Instagram, label: "Instagram", value: "@sheedtyme2pics", href: INSTAGRAM_URL },
              { icon: Mail, label: "Email", value: "sheedtyme2pics@gmail.com", href: "mailto:sheedtyme2pics@gmail.com" },
              { icon: DollarSign, label: "Cash App", value: CASHAPP_TAG },
              { icon: MapPin, label: "Location", value: "Petersburg, VA • RVA • Hopewell" }
            ].map((item, i) => (
              <a key={i} href={item.href || "#"} target={item.href?.startsWith("http") ? "_blank" : undefined} rel={item.href?.startsWith("http") ? "noopener noreferrer" : undefined} className={`flex items-center gap-4 p-4 bg-white border border-neutral-200 ${item.href ? "hover:border-black" : ""} transition-colors`}>
                <item.icon className="w-6 h-6" />
                <div><p className="font-medium">{item.label}</p><p className="text-sm text-neutral-500">{item.value}</p></div>
              </a>
            ))}
          </div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <div className="relative">
            <div className="w-48 h-48 md:w-64 md:h-64 overflow-hidden rounded-full border-4 border-black">
              <img src={PHOTOGRAPHER_PHOTO} alt="Sheedtyme2pics" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-black text-white px-6 py-2">
              <p className="text-sm font-medium tracking-widest">SHEED</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-black text-white py-12 px-6 md:px-12">
    <div className="max-w-screen-xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <Logo size="small" />
        <div className="flex items-center gap-6">
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-neutral-300"><Instagram className="w-5 h-5" /></a>
          <a href="mailto:sheedtyme2pics@gmail.com" className="hover:text-neutral-300"><Mail className="w-5 h-5" /></a>
          <a href="/admin" className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white">Admin</a>
        </div>
      </div>
      <div className="mt-8 pt-8 border-t border-neutral-800 text-center">
        <p className="text-xs text-neutral-500">© 2025 Sheedtyme2pics Photography & Media • 25% deposit required</p>
      </div>
    </div>
  </footer>
);

// Admin Dashboard
const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [adminPwd, setAdminPwd] = useState("");
  const [activeTab, setActiveTab] = useState("analytics");
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientMessages, setClientMessages] = useState([]);
  const [adminReply, setAdminReply] = useState("");
  const [users, setUsers] = useState({ users: [], summary: {} });
  const [selectedUser, setSelectedUser] = useState(null);
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  
  // Upload states
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState("editorial");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [newAlbumTitle, setNewAlbumTitle] = useState("");
  const [newAlbumDesc, setNewAlbumDesc] = useState("");
  const [newAlbumCover, setNewAlbumCover] = useState(null);
  const [newAlbumPassword, setNewAlbumPassword] = useState("");
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumUploadFiles, setAlbumUploadFiles] = useState([]);
  const [albumUploadProgress, setAlbumUploadProgress] = useState(0);
  const [newAnnouncement, setNewAnnouncement] = useState("");

  const handleLogin = async () => {
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      setAdminPwd(password);
      fetchData(password);
      toast.success("Login successful");
    } catch { toast.error("Invalid password"); }
  };

  const fetchData = async (pwd) => {
    setLoading(true);
    try {
      const [b, t, p, al, an, ana, msg, u] = await Promise.all([
        axios.get(`${API}/admin/bookings?password=${pwd}`),
        axios.get(`${API}/admin/transactions?password=${pwd}`),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/admin/albums?password=${pwd}`),
        axios.get(`${API}/admin/announcements?password=${pwd}`),
        axios.get(`${API}/admin/analytics?password=${pwd}`),
        axios.get(`${API}/admin/messages?password=${pwd}`),
        axios.get(`${API}/admin/users?password=${pwd}`)
      ]);
      setBookings(b.data);
      setTransactions(t.data);
      setPortfolioImages(p.data);
      setAlbums(al.data);
      setAnnouncements(an.data);
      setAnalytics(ana.data);
      setMessageThreads(msg.data);
      setUsers(u.data);
    } catch {} finally { setLoading(false); }
  };

  const uploadPortfolioImages = async () => {
    if (!uploadFiles.length) return toast.error("Select files");
    const formData = new FormData();
    for (const file of uploadFiles) formData.append("files", file);
    formData.append("category", uploadCategory);
    formData.append("password", adminPwd);
    setIsUploading(true);
    setUploadProgress(0);
    try {
      await axios.post(`${API}/admin/portfolio/upload`, formData, { 
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success(`${uploadFiles.length} images uploaded successfully!`, { duration: 5000 });
      setUploadFiles([]);
      setUploadProgress(0);
      fetchData(adminPwd);
    } catch { toast.error("Upload failed"); }
    finally { setIsUploading(false); }
  };

  const createAlbum = async () => {
    if (!newAlbumTitle) return toast.error("Enter album title");
    const formData = new FormData();
    formData.append("title", newAlbumTitle);
    formData.append("description", newAlbumDesc);
    if (newAlbumCover) formData.append("cover", newAlbumCover);
    if (newAlbumPassword) formData.append("album_password", newAlbumPassword);
    formData.append("password", adminPwd);
    try {
      await axios.post(`${API}/admin/albums`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Album created successfully!");
      setNewAlbumTitle("");
      setNewAlbumDesc("");
      setNewAlbumCover(null);
      setNewAlbumPassword("");
      fetchData(adminPwd);
    } catch { toast.error("Failed"); }
  };

  const uploadAlbumPhotos = async () => {
    if (!selectedAlbum || !albumUploadFiles.length) return toast.error("Select album and files");
    const formData = new FormData();
    for (const file of albumUploadFiles) formData.append("files", file);
    formData.append("password", adminPwd);
    setAlbumUploadProgress(0);
    try {
      await axios.post(`${API}/admin/albums/${selectedAlbum}/photos`, formData, { 
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setAlbumUploadProgress(progress);
        }
      });
      toast.success(`${albumUploadFiles.length} photos uploaded successfully!`, { duration: 5000 });
      setAlbumUploadFiles([]);
      setAlbumUploadProgress(0);
      fetchData(adminPwd);
    } catch { toast.error("Failed"); }
  };

  const createAnnouncement = async () => {
    if (!newAnnouncement) return;
    try {
      await axios.post(`${API}/admin/announcements?password=${adminPwd}`, { message: newAnnouncement });
      toast.success("Announcement created");
      setNewAnnouncement("");
      fetchData(adminPwd);
    } catch { toast.error("Failed"); }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-6">
        <div className="w-full max-w-md bg-white p-8 border border-neutral-200">
          <div className="text-center mb-8"><Logo /><h1 className="font-serif text-2xl mt-6">Admin Login</h1></div>
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" className="mb-4 rounded-none" onKeyPress={e => e.key === "Enter" && handleLogin()} />
          <Button onClick={handleLogin} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none py-6">Login</Button>
          <a href="/" className="block text-center mt-4 text-sm text-neutral-500 hover:text-black">← Back</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-black text-white px-6 py-4">
        <div className="max-w-screen-xl mx-auto flex justify-between items-center">
          <Logo size="small" />
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm hover:text-neutral-300">View Site</a>
            <button onClick={() => setIsLoggedIn(false)} className="text-sm hover:text-neutral-300 flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 rounded-none bg-white border flex-wrap">
            <TabsTrigger value="analytics" className="rounded-none"><BarChart3 className="w-4 h-4 mr-2" /> Analytics</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-none"><Calendar className="w-4 h-4 mr-2" /> Bookings</TabsTrigger>
            <TabsTrigger value="users" className="rounded-none"><Users className="w-4 h-4 mr-2" /> Users ({users.summary?.total_users || 0})</TabsTrigger>
            <TabsTrigger value="messages" className="rounded-none"><Mail className="w-4 h-4 mr-2" /> Messages</TabsTrigger>
            <TabsTrigger value="portfolio" className="rounded-none"><Image className="w-4 h-4 mr-2" /> Portfolio</TabsTrigger>
            <TabsTrigger value="albums" className="rounded-none"><FolderOpen className="w-4 h-4 mr-2" /> Albums</TabsTrigger>
            <TabsTrigger value="announcements" className="rounded-none"><Megaphone className="w-4 h-4 mr-2" /> Announcements</TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-none"><CreditCard className="w-4 h-4 mr-2" /> Transactions</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[{ label: "Bookings", value: analytics?.overview?.total_bookings || 0, icon: Users },
                { label: "Revenue", value: `$${analytics?.revenue?.total || 0}`, icon: TrendingUp },
                { label: "Page Views", value: analytics?.traffic?.total_views || 0, icon: Eye },
                { label: "Clients", value: analytics?.overview?.total_clients || 0, icon: User }
              ].map((item, i) => (
                <div key={i} className="bg-white p-6 border">
                  <div className="flex items-center gap-2 mb-2"><item.icon className="w-4 h-4 text-neutral-500" /><span className="text-xs uppercase tracking-widest text-neutral-500">{item.label}</span></div>
                  <p className="font-serif text-3xl">{item.value}</p>
                </div>
              ))}
            </div>
            
            {/* Traffic & Engagement */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white p-6 border">
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2"><Eye className="w-5 h-5" /> Site Traffic</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm">Today</span><span className="font-medium">{analytics?.traffic?.today || 0} views</span></div>
                  <div className="flex justify-between"><span className="text-sm">This Week</span><span className="font-medium">{analytics?.traffic?.this_week || 0} views</span></div>
                  <div className="flex justify-between"><span className="text-sm">This Month</span><span className="font-medium">{analytics?.traffic?.this_month || 0} views</span></div>
                  <div className="flex justify-between border-t pt-3"><span className="text-sm font-medium">Unique Visitors</span><span className="font-medium">{analytics?.traffic?.unique_visitors || 0}</span></div>
                </div>
              </div>
              <div className="bg-white p-6 border">
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Page Breakdown</h3>
                {analytics?.page_breakdown?.length > 0 ? analytics.page_breakdown.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex justify-between items-center mb-2">
                    <span className="text-sm capitalize">{p.page}</span>
                    <span className="text-sm font-medium">{p.views}</span>
                  </div>
                )) : <p className="text-neutral-500 text-sm">No data yet</p>}
              </div>
              <div className="bg-white p-6 border">
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2"><MousePointer className="w-5 h-5" /> Engagement</h3>
                <p className="text-3xl font-serif mb-2">{analytics?.engagement?.total_events || 0}</p>
                <p className="text-sm text-neutral-500 mb-4">Total events tracked</p>
                {analytics?.engagement?.events_breakdown?.length > 0 && analytics.engagement.events_breakdown.slice(0, 4).map((e, i) => (
                  <div key={i} className="flex justify-between items-center mb-2">
                    <span className="text-xs capitalize">{e.event?.replace("_", " ")}</span>
                    <span className="text-xs font-medium">{e.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue & Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 border">
                <h3 className="font-serif text-lg mb-4">Revenue Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm">Deposits</span><span className="font-medium text-green-600">${analytics?.revenue?.deposits || 0}</span></div>
                  <div className="flex justify-between"><span className="text-sm">Fees Charged</span><span className="font-medium text-orange-600">${analytics?.revenue?.fees || 0}</span></div>
                  <div className="flex justify-between"><span className="text-sm">Purchases</span><span className="font-medium">${analytics?.revenue?.purchases || 0}</span></div>
                  <div className="flex justify-between border-t pt-3"><span className="font-medium">Total Revenue</span><span className="font-serif text-xl">${analytics?.revenue?.total || 0}</span></div>
                </div>
              </div>
              <div className="bg-white p-6 border">
                <h3 className="font-serif text-lg mb-4">Bookings by Status</h3>
                {["pending", "confirmed", "completed"].map(s => (
                  <div key={s} className="flex justify-between items-center mb-2">
                    <span className="text-sm capitalize">{s}</span>
                    <span className={`px-3 py-1 text-sm ${s === "confirmed" ? "bg-green-100 text-green-800" : s === "completed" ? "bg-blue-100 text-blue-800" : "bg-yellow-100 text-yellow-800"}`}>
                      {analytics?.bookings_by_status?.[s] || 0}
                    </span>
                  </div>
                ))}
              </div>
              <div className="bg-white p-6 border">
                <h3 className="font-serif text-lg mb-4">Recent Activity</h3>
                {analytics?.recent_activity?.slice(0, 5).map((a, i) => (
                  <div key={i} className="flex justify-between items-center border-b pb-2 mb-2">
                    <div><p className="font-medium text-sm">{a.name}</p><p className="text-xs text-neutral-500">{a.date}</p></div>
                    <span className={`text-xs px-2 py-1 ${a.status === "confirmed" ? "bg-green-100" : "bg-yellow-100"}`}>{a.status}</span>
                  </div>
                )) || <p className="text-neutral-500">No activity</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <div className="bg-white border p-6 mb-4">
              <div className="flex justify-between items-center">
                <h2 className="font-serif text-xl">Bookings ({bookings.length})</h2>
                <Button onClick={async () => { 
                  try { 
                    const res = await axios.post(`${API}/admin/send-reminders?password=${adminPwd}`); 
                    toast.success(`Reminders sent: 7-day(${res.data.reminders_sent["7day"]}), 3-day(${res.data.reminders_sent["3day"]}), 24hr(${res.data.reminders_sent["24hr"]})`); 
                  } catch { toast.error("Failed to send reminders"); } 
                }} className="rounded-none bg-blue-600 hover:bg-blue-700 text-white">
                  <Bell className="w-4 h-4 mr-2" /> Send Reminders
                </Button>
              </div>
            </div>
            <div className="bg-white border p-6">
              <div className="space-y-4">
                {bookings.map(b => (
                  <div key={b.id} className="border p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-medium">{b.name}</h3>
                        <p className="text-sm text-neutral-500">{b.email}</p>
                        {b.card_last4 && <p className="text-xs text-green-600 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Card on file: •••• {b.card_last4} ({b.card_brand})</p>}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 ${b.status === "confirmed" ? "bg-green-100" : b.status === "completed" ? "bg-blue-100" : b.status === "no_show" ? "bg-red-100" : "bg-yellow-100"}`}>{b.status}</span>
                        {b.deposit_paid && <span className="text-xs px-2 py-1 bg-green-100">Deposit Paid</span>}
                        {b.no_show_charged && <span className="text-xs px-2 py-1 bg-red-100">No-Show Charged</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm mb-3">
                      <div><span className="text-neutral-500">Date:</span> {b.date}</div>
                      <div><span className="text-neutral-500">Package:</span> {b.package}</div>
                      <div><span className="text-neutral-500">Total:</span> ${b.total_estimate}</div>
                      <div><span className="text-neutral-500">Deposit:</span> ${b.deposit_amount}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Select defaultValue={b.status} onValueChange={async v => { await axios.put(`${API}/admin/bookings/${b.id}/status?password=${adminPwd}`, { status: v }); fetchData(adminPwd); }}>
                        <SelectTrigger className="w-32 h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{["pending", "approved", "confirmed", "completed", "cancelled", "no_show"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      {b.status === "pending" && !b.deposit_paid && (
                        <Button size="sm" onClick={async () => { 
                          if (window.confirm(`Approve booking and send payment link for $${b.deposit_amount} to ${b.email}?`)) {
                            try { 
                              await axios.post(`${API}/admin/booking/${b.id}/approve?password=${adminPwd}`); 
                              toast.success("Approved! Payment link sent to client."); 
                              fetchData(adminPwd); 
                            } catch(e) { toast.error(e.response?.data?.detail || "Failed"); } 
                          }
                        }} className="rounded-none bg-green-600 hover:bg-green-700 text-white h-8 text-xs">
                          <Check className="w-3 h-3 mr-1" /> Approve & Send Payment Link
                        </Button>
                      )}
                      {b.card_last4 && !b.deposit_paid && (
                        <Button size="sm" onClick={async () => { 
                          if (window.confirm(`Charge $${b.deposit_amount} deposit to card ending in ${b.card_last4}?`)) {
                            try { 
                              await axios.post(`${API}/admin/booking/${b.id}/charge-deposit?password=${adminPwd}`); 
                              toast.success("Deposit charged!"); 
                              fetchData(adminPwd); 
                            } catch(e) { toast.error(e.response?.data?.detail || "Failed"); } 
                          }
                        }} className="rounded-none bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs">
                          <DollarSign className="w-3 h-3 mr-1" /> Charge Deposit
                        </Button>
                      )}
                      {b.card_last4 && !b.no_show_charged && (
                        <Button size="sm" variant="destructive" onClick={async () => { 
                          const noShowFee = (b.total_estimate * 0.5) - (b.deposit_paid ? b.deposit_amount : 0);
                          if (window.confirm(`Charge NO-SHOW fee? Amount: $${Math.max(noShowFee, 0).toFixed(2)} (50% of total minus deposit)`)) {
                            try { 
                              const res = await axios.post(`${API}/admin/booking/${b.id}/charge-noshow?password=${adminPwd}`); 
                              toast.success(res.data.message); 
                              fetchData(adminPwd); 
                            } catch(e) { toast.error(e.response?.data?.detail || "Failed"); } 
                          }
                        }} className="rounded-none h-8 text-xs">
                          <AlertTriangle className="w-3 h-3 mr-1" /> No-Show Fee
                        </Button>
                      )}
                      {b.card_last4 && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="rounded-none h-8 text-xs">
                              <DollarSign className="w-3 h-3 mr-1" /> Charge Fee
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader><DialogTitle>Charge Fee to Card on File</DialogTitle></DialogHeader>
                            <p className="text-sm text-neutral-500 mb-4">Card: {b.card_brand} •••• {b.card_last4}</p>
                            <div className="space-y-4">
                              <div>
                                <label className="text-xs uppercase tracking-widest font-medium block mb-1">Fee Type</label>
                                <Select onValueChange={v => document.getElementById(`fee-type-${b.id}`).value = v}>
                                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Select fee type" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="late_fee">Late Fee</SelectItem>
                                    <SelectItem value="cancellation_fee">Cancellation Fee</SelectItem>
                                    <SelectItem value="no_show_fee">No-Show Fee (50%)</SelectItem>
                                    <SelectItem value="additional_charge">Additional Charge</SelectItem>
                                  </SelectContent>
                                </Select>
                                <input type="hidden" id={`fee-type-${b.id}`} />
                              </div>
                              <div>
                                <label className="text-xs uppercase tracking-widest font-medium block mb-1">Amount ($)</label>
                                <Input type="number" step="0.01" id={`fee-amount-${b.id}`} placeholder="0.00" className="rounded-none" />
                              </div>
                              <div>
                                <label className="text-xs uppercase tracking-widest font-medium block mb-1">Description (optional)</label>
                                <Input id={`fee-desc-${b.id}`} placeholder="Description..." className="rounded-none" />
                              </div>
                              <Button onClick={async () => {
                                const feeType = document.getElementById(`fee-type-${b.id}`).value;
                                const amount = parseFloat(document.getElementById(`fee-amount-${b.id}`).value);
                                const desc = document.getElementById(`fee-desc-${b.id}`).value;
                                if (!feeType || !amount || amount <= 0) { toast.error("Enter fee type and amount"); return; }
                                if (window.confirm(`Charge $${amount.toFixed(2)} ${feeType.replace("_", " ")} to card?`)) {
                                  try {
                                    await axios.post(`${API}/admin/charge-fee/${b.id}?password=${adminPwd}&fee_type=${feeType}&amount=${amount}${desc ? `&description=${encodeURIComponent(desc)}` : ""}`);
                                    toast.success("Fee charged successfully!");
                                    fetchData(adminPwd);
                                  } catch(e) { toast.error(e.response?.data?.detail || "Failed to charge"); }
                                }
                              }} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none">Charge Card</Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      <Button variant="destructive" size="sm" onClick={async () => { if (window.confirm("Delete?")) { await axios.delete(`${API}/admin/bookings/${b.id}?password=${adminPwd}`); fetchData(adminPwd); } }} className="rounded-none h-8"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
                {!bookings.length && <p className="text-neutral-500">No bookings</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border">
                <div className="p-4 border-b"><h3 className="font-medium">Client Conversations</h3></div>
                <div className="divide-y max-h-96 overflow-y-auto">
                  {messageThreads.length === 0 ? (
                    <p className="p-4 text-neutral-500 text-sm">No messages yet</p>
                  ) : messageThreads.map(thread => (
                    <button key={thread.client.id} onClick={async () => {
                      setSelectedClient(thread.client);
                      const res = await axios.get(`${API}/admin/messages/${thread.client.id}?password=${adminPwd}`);
                      setClientMessages(res.data);
                    }} className={`w-full p-4 text-left hover:bg-neutral-50 ${selectedClient?.id === thread.client.id ? "bg-neutral-100" : ""}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{thread.client.name}</p>
                          <p className="text-xs text-neutral-500">{thread.client.email}</p>
                        </div>
                        {thread.unread_count > 0 && <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{thread.unread_count}</span>}
                      </div>
                      {thread.last_message && <p className="text-sm text-neutral-500 mt-1 truncate">{thread.last_message.message}</p>}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 bg-white border">
                {selectedClient ? (
                  <>
                    <div className="p-4 border-b flex justify-between items-center">
                      <div><h3 className="font-medium">{selectedClient.name}</h3><p className="text-sm text-neutral-500">{selectedClient.email}</p></div>
                    </div>
                    <div className="h-64 overflow-y-auto p-4 space-y-3">
                      {clientMessages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-xs p-3 ${msg.sender === "admin" ? "bg-black text-white" : "bg-neutral-100"}`}>
                            <p className="text-sm">{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.sender === "admin" ? "text-neutral-400" : "text-neutral-500"}`}>{new Date(msg.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-4 border-t flex gap-2">
                      <Input value={adminReply} onChange={e => setAdminReply(e.target.value)} placeholder="Reply..." className="flex-1 rounded-none" onKeyPress={e => e.key === "Enter" && adminReply && (async () => {
                        await axios.post(`${API}/admin/messages/${selectedClient.id}?password=${adminPwd}`, { message: adminReply });
                        setAdminReply("");
                        const res = await axios.get(`${API}/admin/messages/${selectedClient.id}?password=${adminPwd}`);
                        setClientMessages(res.data);
                        fetchData(adminPwd);
                        toast.success("Sent!");
                      })()} />
                      <Button onClick={async () => {
                        if (!adminReply) return;
                        await axios.post(`${API}/admin/messages/${selectedClient.id}?password=${adminPwd}`, { message: adminReply });
                        setAdminReply("");
                        const res = await axios.get(`${API}/admin/messages/${selectedClient.id}?password=${adminPwd}`);
                        setClientMessages(res.data);
                        fetchData(adminPwd);
                        toast.success("Sent!");
                      }} className="bg-black text-white hover:bg-neutral-800 rounded-none"><Send className="w-4 h-4" /></Button>
                    </div>
                  </>
                ) : (
                  <div className="h-80 flex items-center justify-center text-neutral-400">
                    <div className="text-center"><Mail className="w-12 h-12 mx-auto mb-2" /><p>Select a conversation</p></div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="portfolio">
            <div className="bg-white border p-6 mb-6">
              <h2 className="font-serif text-xl mb-6">Upload Portfolio Images</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <Input type="file" accept="image/*" multiple onChange={e => setUploadFiles(Array.from(e.target.files))} className="rounded-none md:col-span-2" disabled={isUploading} />
                <Select value={uploadCategory} onValueChange={setUploadCategory} disabled={isUploading}><SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger><SelectContent>{["editorial", "sports", "portrait", "event", "team"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                <Button onClick={uploadPortfolioImages} disabled={isUploading} className="bg-black text-white hover:bg-neutral-800 rounded-none">
                  {isUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload {uploadFiles.length ? `(${uploadFiles.length})` : ""}</>}
                </Button>
              </div>
              {isUploading && (
                <div className="space-y-2">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-sm text-neutral-500 text-center">{uploadProgress}% uploaded</p>
                </div>
              )}
            </div>
            <div className="bg-white border p-6">
              <h2 className="font-serif text-xl mb-6">Portfolio Images ({portfolioImages.length})</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {portfolioImages.map(img => (
                  <div key={img.id} className="relative group">
                    <img src={`${BACKEND_URL}/api/uploads/${img.filename}`} alt={img.category} className="w-full aspect-square object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button variant="destructive" size="sm" onClick={async () => { await axios.delete(`${API}/admin/portfolio/${img.id}?password=${adminPwd}`); fetchData(adminPwd); }} className="rounded-none"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div className="absolute bottom-2 left-2"><span className="text-xs bg-black text-white px-2 py-1">{img.category}</span></div>
                  </div>
                ))}
                {!portfolioImages.length && <p className="text-neutral-500 col-span-4">No images</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="albums">
            <div className="bg-white border p-6 mb-6">
              <h2 className="font-serif text-xl mb-6">Create New Album</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Input placeholder="Album Title *" value={newAlbumTitle} onChange={e => setNewAlbumTitle(e.target.value)} className="rounded-none" />
                <Input placeholder="Description" value={newAlbumDesc} onChange={e => setNewAlbumDesc(e.target.value)} className="rounded-none" />
                <Input type="file" accept="image/*" onChange={e => setNewAlbumCover(e.target.files[0])} className="rounded-none" />
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input placeholder="Album Password (optional)" type="password" value={newAlbumPassword} onChange={e => setNewAlbumPassword(e.target.value)} className="rounded-none pl-10" />
                </div>
                <Button onClick={createAlbum} className="bg-black text-white hover:bg-neutral-800 rounded-none"><Plus className="w-4 h-4 mr-2" /> Create Album</Button>
              </div>
              <p className="text-xs text-neutral-500 mt-2">Set a password to make the album private. Clients will need the password to view and purchase photos.</p>
            </div>
            <div className="bg-white border p-6 mb-6">
              <h2 className="font-serif text-xl mb-6">Upload Photos to Album</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select value={selectedAlbum || ""} onValueChange={setSelectedAlbum}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Select Album" /></SelectTrigger>
                  <SelectContent>{albums.map(a => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="file" accept="image/*" multiple onChange={e => setAlbumUploadFiles(Array.from(e.target.files))} className="rounded-none" />
                <Button onClick={uploadAlbumPhotos} disabled={albumUploadProgress > 0} className="bg-black text-white hover:bg-neutral-800 rounded-none">
                  {albumUploadProgress > 0 ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload {albumUploadFiles.length ? `(${albumUploadFiles.length})` : ""}</>}
                </Button>
              </div>
              {albumUploadProgress > 0 && (
                <div className="mt-4 space-y-2">
                  <Progress value={albumUploadProgress} className="h-2" />
                  <p className="text-sm text-neutral-500 text-center">{albumUploadProgress}% uploaded</p>
                </div>
              )}
              <p className="text-sm text-neutral-500 mt-4">Watermarks will be automatically added to uploaded photos</p>
            </div>
            <div className="bg-white border p-6">
              <h2 className="font-serif text-xl mb-6">Albums ({albums.length})</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {albums.map(album => (
                  <div key={album.id} className="border relative">
                    <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                      {album.cover_filename ? <img src={`${API}/albums/${album.id}/cover`} alt={album.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><FolderOpen className="w-12 h-12 text-neutral-300" /></div>}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <h4 className="font-serif text-lg">{album.title}</h4>
                        {album.password && <Lock className="w-4 h-4 text-neutral-400" title="Password protected" />}
                      </div>
                      <p className="text-sm text-neutral-500">{album.photo_count} photos</p>
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button variant="outline" size="sm" onClick={async () => {
                        const newPwd = window.prompt("Set album password (leave empty to remove):", "");
                        if (newPwd !== null) {
                          await axios.put(`${API}/admin/albums/${album.id}/password?password=${adminPwd}&album_password=${encodeURIComponent(newPwd)}`);
                          toast.success(newPwd ? "Password set" : "Password removed");
                          fetchData(adminPwd);
                        }
                      }} className="rounded-none bg-white">{album.password ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}</Button>
                      <Button variant="destructive" size="sm" onClick={async () => { if (window.confirm("Delete album and all photos?")) { await axios.delete(`${API}/admin/albums/${album.id}?password=${adminPwd}`); fetchData(adminPwd); } }} className="rounded-none"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
                {!albums.length && <p className="text-neutral-500 col-span-3">No albums</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="announcements">
            <div className="bg-white border p-6 mb-6">
              <h2 className="font-serif text-xl mb-6">Create Announcement</h2>
              <div className="flex gap-4">
                <Input placeholder="Announcement message..." value={newAnnouncement} onChange={e => setNewAnnouncement(e.target.value)} className="rounded-none flex-1" />
                <Button onClick={createAnnouncement} className="bg-black text-white hover:bg-neutral-800 rounded-none"><Plus className="w-4 h-4 mr-2" /> Create</Button>
              </div>
              <p className="text-sm text-neutral-500 mt-2">Announcements appear as a banner on the website. To email all users, go to the Users tab.</p>
            </div>
            <div className="bg-white border p-6">
              <h2 className="font-serif text-xl mb-6">Announcements</h2>
              <div className="space-y-4">
                {announcements.map(ann => (
                  <div key={ann.id} className="border p-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <span className={`w-3 h-3 rounded-full ${ann.is_active ? "bg-green-500" : "bg-neutral-300"}`} />
                      <p>{ann.message}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={async () => {
                          if (!window.confirm(`Email this announcement to all ${users.summary?.total_users || 0} users?`)) return;
                          setBroadcastLoading(true);
                          try {
                            const res = await axios.post(`${API}/admin/broadcast-announcement?password=${adminPwd}&subject=${encodeURIComponent("New Announcement")}&message=${encodeURIComponent(ann.message)}&ann_id=${ann.id}`);
                            toast.success(`Sent to ${res.data.sent} users!`);
                          } catch { toast.error("Failed to send"); }
                          finally { setBroadcastLoading(false); }
                        }}
                        className="rounded-none"
                        disabled={broadcastLoading}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Email All
                      </Button>
                      <Button variant="outline" size="sm" onClick={async () => { await axios.put(`${API}/admin/announcements/${ann.id}/toggle?password=${adminPwd}`); fetchData(adminPwd); }} className="rounded-none">
                        {ann.is_active ? <Eye className="w-4 h-4" /> : <Eye className="w-4 h-4 opacity-50" />}
                      </Button>
                      <Button variant="destructive" size="sm" onClick={async () => { await axios.delete(`${API}/admin/announcements/${ann.id}?password=${adminPwd}`); fetchData(adminPwd); }} className="rounded-none"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
                {!announcements.length && <p className="text-neutral-500">No announcements</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            <div className="bg-white border p-6">
              <h2 className="font-serif text-xl mb-6">Transactions ({(transactions.transactions?.length || 0) + (transactions.legacy_transactions?.length || 0)})</h2>
              <div className="space-y-4">
                {/* New Transactions */}
                {transactions.transactions?.map(tx => (
                  <Dialog key={tx.id}>
                    <DialogTrigger asChild>
                      <div className="border p-4 flex justify-between items-center cursor-pointer hover:bg-neutral-50">
                        <div>
                          <p className="font-medium">{tx.description || tx.fee_type}</p>
                          <p className="text-sm text-neutral-500">{tx.fee_type} • {tx.created_at?.split("T")[0]}</p>
                          {tx.card_last4 && <p className="text-xs text-neutral-400 flex items-center gap-1"><CreditCard className="w-3 h-3" /> {tx.card_brand} •••• {tx.card_last4}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-serif text-lg">${tx.amount?.toFixed(2)}</p>
                          <span className={`text-xs px-2 py-1 ${tx.status === "succeeded" ? "bg-green-100" : "bg-yellow-100"}`}>{tx.status}</span>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Transaction Details</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div><p className="text-xs text-neutral-500">Amount</p><p className="font-serif text-2xl">${tx.amount?.toFixed(2)}</p></div>
                          <div><p className="text-xs text-neutral-500">Status</p><span className={`px-3 py-1 text-sm ${tx.status === "succeeded" ? "bg-green-100" : "bg-yellow-100"}`}>{tx.status}</span></div>
                        </div>
                        <div><p className="text-xs text-neutral-500">Type</p><p className="font-medium capitalize">{tx.fee_type?.replace("_", " ")}</p></div>
                        <div><p className="text-xs text-neutral-500">Description</p><p>{tx.description || "N/A"}</p></div>
                        <div><p className="text-xs text-neutral-500">Date</p><p>{new Date(tx.created_at).toLocaleString()}</p></div>
                        {tx.card_last4 && (
                          <div className="bg-neutral-50 p-4">
                            <p className="text-xs text-neutral-500 mb-2">Payment Method</p>
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-5 h-5" />
                              <span className="font-medium">{tx.card_brand}</span>
                              <span>•••• {tx.card_last4}</span>
                            </div>
                          </div>
                        )}
                        {tx.booking_id && <div><p className="text-xs text-neutral-500">Booking ID</p><p className="font-mono text-sm">{tx.booking_id}</p></div>}
                        {tx.receipt_url && <a href={tx.receipt_url} target="_blank" rel="noopener noreferrer" className="block text-center bg-black text-white py-2 hover:bg-neutral-800">View Receipt →</a>}
                      </div>
                    </DialogContent>
                  </Dialog>
                ))}
                
                {/* Legacy Transactions */}
                {transactions.legacy_transactions?.map(tx => (
                  <Dialog key={tx.id}>
                    <DialogTrigger asChild>
                      <div className="border p-4 flex justify-between items-center cursor-pointer hover:bg-neutral-50">
                        <div>
                          <p className="font-medium">{tx.product_name || tx.package}</p>
                          <p className="text-sm text-neutral-500">{tx.payment_type} • {tx.created_at?.split("T")[0]}</p>
                          {tx.customer_info && <p className="text-xs text-neutral-400">{tx.customer_info.name} - {tx.customer_info.city}, {tx.customer_info.state}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-serif text-lg">${tx.amount?.toFixed(2)}</p>
                          <span className={`text-xs px-2 py-1 ${tx.payment_status === "paid" ? "bg-green-100" : "bg-yellow-100"}`}>{tx.payment_status}</span>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Transaction Details</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div><p className="text-xs text-neutral-500">Amount</p><p className="font-serif text-2xl">${tx.amount?.toFixed(2)}</p></div>
                          <div><p className="text-xs text-neutral-500">Status</p><span className={`px-3 py-1 text-sm ${tx.payment_status === "paid" ? "bg-green-100" : "bg-yellow-100"}`}>{tx.payment_status}</span></div>
                        </div>
                        <div><p className="text-xs text-neutral-500">Type</p><p className="font-medium capitalize">{tx.payment_type}</p></div>
                        <div><p className="text-xs text-neutral-500">Product</p><p>{tx.product_name || tx.package}</p></div>
                        <div><p className="text-xs text-neutral-500">Date</p><p>{new Date(tx.created_at).toLocaleString()}</p></div>
                        {tx.customer_info && (
                          <div className="bg-neutral-50 p-4">
                            <p className="text-xs text-neutral-500 mb-2">Customer Details</p>
                            <p className="font-medium">{tx.customer_info.name}</p>
                            <p className="text-sm">{tx.customer_info.email}</p>
                            <p className="text-sm text-neutral-500">{tx.customer_info.address}, {tx.customer_info.city}, {tx.customer_info.state} {tx.customer_info.zip_code}</p>
                          </div>
                        )}
                        {tx.card_last4 && (
                          <div className="bg-neutral-50 p-4">
                            <p className="text-xs text-neutral-500 mb-2">Payment Method</p>
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-5 h-5" />
                              <span className="font-medium">{tx.card_brand}</span>
                              <span>•••• {tx.card_last4}</span>
                            </div>
                          </div>
                        )}
                        {tx.booking_id && <div><p className="text-xs text-neutral-500">Booking ID</p><p className="font-mono text-sm">{tx.booking_id}</p></div>}
                        {tx.receipt_url && <a href={tx.receipt_url} target="_blank" rel="noopener noreferrer" className="block text-center bg-black text-white py-2 hover:bg-neutral-800">View Receipt →</a>}
                      </div>
                    </DialogContent>
                  </Dialog>
                ))}
                {!transactions.transactions?.length && !transactions.legacy_transactions?.length && <p className="text-neutral-500">No transactions</p>}
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-6 border">
                <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-neutral-500" /><span className="text-xs uppercase tracking-widest text-neutral-500">Total Users</span></div>
                <p className="font-serif text-3xl">{users.summary?.total_users || 0}</p>
              </div>
              <div className="bg-white p-6 border">
                <div className="flex items-center gap-2 mb-2"><User className="w-4 h-4 text-green-500" /><span className="text-xs uppercase tracking-widest text-neutral-500">Active Users</span></div>
                <p className="font-serif text-3xl">{users.summary?.active_users || 0}</p>
              </div>
              <div className="bg-white p-6 border">
                <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-neutral-500" /><span className="text-xs uppercase tracking-widest text-neutral-500">Revenue from Users</span></div>
                <p className="font-serif text-3xl">${users.summary?.total_revenue_from_users || 0}</p>
              </div>
            </div>

            {/* Broadcast Announcement */}
            <div className="bg-white p-6 border mb-6">
              <h3 className="font-serif text-lg mb-4 flex items-center gap-2"><Mail className="w-5 h-5" /> Email Broadcast to All Users</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest font-medium block mb-1">Subject</label>
                  <Input value={broadcastSubject} onChange={e => setBroadcastSubject(e.target.value)} placeholder="Announcement subject..." className="rounded-none" />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={async () => {
                      if (!broadcastSubject || !broadcastMessage) return toast.error("Enter subject and message");
                      if (!window.confirm(`Send email to ${users.summary?.total_users || 0} users?`)) return;
                      setBroadcastLoading(true);
                      try {
                        const res = await axios.post(`${API}/admin/broadcast-announcement?password=${adminPwd}&subject=${encodeURIComponent(broadcastSubject)}&message=${encodeURIComponent(broadcastMessage)}`);
                        toast.success(`Sent to ${res.data.sent} users! (${res.data.failed} failed)`);
                        setBroadcastSubject("");
                        setBroadcastMessage("");
                      } catch { toast.error("Failed to send broadcast"); }
                      finally { setBroadcastLoading(false); }
                    }}
                    disabled={broadcastLoading}
                    className="bg-black text-white hover:bg-neutral-800 rounded-none w-full"
                  >
                    {broadcastLoading ? "Sending..." : `Send to ${users.summary?.total_users || 0} Users`}
                  </Button>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest font-medium block mb-1">Message</label>
                <Textarea value={broadcastMessage} onChange={e => setBroadcastMessage(e.target.value)} placeholder="Your announcement message..." className="rounded-none" rows={4} />
              </div>
            </div>

            {/* Users List */}
            <div className="bg-white border">
              <div className="p-4 border-b">
                <h3 className="font-serif text-lg">All Registered Users</h3>
              </div>
              <div className="divide-y">
                {users.users?.map(user => (
                  <Dialog key={user.id}>
                    <DialogTrigger asChild>
                      <div className="p-4 hover:bg-neutral-50 cursor-pointer flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          {user.profile_picture_url ? (
                            <img src={`${BACKEND_URL}${user.profile_picture_url}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center"><User className="w-5 h-5 text-neutral-400" /></div>
                          )}
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-neutral-500">{user.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">{user.stats?.total_bookings || 0} bookings</p>
                          <p className="text-sm text-green-600">${user.stats?.total_spent || 0} spent</p>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>User Details - {user.name}</DialogTitle></DialogHeader>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium mb-3">Contact Info</h4>
                          <div className="space-y-2 text-sm">
                            <p><strong>Email:</strong> {user.email}</p>
                            <p><strong>Phone:</strong> {user.phone || "Not provided"}</p>
                            <p><strong>Address:</strong> {user.address ? `${user.address}, ${user.city}, ${user.state} ${user.zip_code}` : "Not provided"}</p>
                            <p><strong>Joined:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-3">Stats</h4>
                          <div className="space-y-2 text-sm">
                            <p><strong>Total Bookings:</strong> {user.stats?.total_bookings || 0}</p>
                            <p><strong>Completed:</strong> {user.stats?.completed_bookings || 0}</p>
                            <p><strong>Pending:</strong> {user.stats?.pending_bookings || 0}</p>
                            <p><strong>Total Booked Value:</strong> ${user.stats?.total_booked_value || 0}</p>
                            <p><strong>Total Spent:</strong> <span className="text-green-600">${user.stats?.total_spent || 0}</span></p>
                            <p><strong>Messages:</strong> {user.stats?.message_count || 0}</p>
                          </div>
                        </div>
                      </div>
                      {user.stripe_customer_id && (
                        <div className="mt-4 bg-neutral-50 p-4">
                          <h4 className="font-medium mb-2">Payment Info</h4>
                          <p className="text-sm">Stripe Customer: {user.stripe_customer_id}</p>
                          {user.card_last4 && <p className="text-sm">Card on File: {user.card_brand} •••• {user.card_last4}</p>}
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                ))}
                {!users.users?.length && <p className="p-6 text-center text-neutral-500">No registered users yet</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Client Portal
const ClientPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [client, setClient] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState({ name: "", email: "", phone: "", password: "", address: "", city: "", state: "", zip_code: "" });
  const [profileForm, setProfileForm] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    const savedClient = localStorage.getItem("client");
    if (savedClient) {
      const parsed = JSON.parse(savedClient);
      setClient(parsed);
      setIsLoggedIn(true);
      fetchClientData(parsed.client_id);
    }
  }, []);

  const fetchClientData = async (clientId) => {
    try {
      const [profileRes, bookingsRes, messagesRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/client/${clientId}/profile`),
        axios.get(`${API}/client/${clientId}/bookings`),
        axios.get(`${API}/client/${clientId}/messages`),
        axios.get(`${API}/client/${clientId}/transactions`)
      ]);
      setProfile(profileRes.data);
      setProfileForm(profileRes.data);
      setBookings(bookingsRes.data);
      setMessages(messagesRes.data);
      setTransactions(transactionsRes.data);
    } catch (err) { console.error(err); }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (authMode === "register") {
        const res = await axios.post(`${API}/client/register`, authForm);
        localStorage.setItem("client", JSON.stringify(res.data));
        setClient(res.data);
        setIsLoggedIn(true);
        toast.success("Account created!");
        fetchClientData(res.data.client_id);
      } else {
        const res = await axios.post(`${API}/client/login`, { email: authForm.email, password: authForm.password });
        localStorage.setItem("client", JSON.stringify(res.data));
        setClient(res.data);
        setIsLoggedIn(true);
        toast.success("Welcome back!");
        fetchClientData(res.data.client_id);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Authentication failed"); }
    finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("client");
    setClient(null);
    setProfile(null);
    setIsLoggedIn(false);
    setBookings([]);
    setMessages([]);
    setTransactions([]);
  };

  const handleProfileUpdate = async () => {
    try {
      await axios.put(`${API}/client/${client.client_id}/profile`, {
        name: profileForm.name,
        phone: profileForm.phone,
        address: profileForm.address,
        city: profileForm.city,
        state: profileForm.state,
        zip_code: profileForm.zip_code
      });
      toast.success("Profile updated!");
      fetchClientData(client.client_id);
    } catch { toast.error("Failed to update profile"); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/client/${client.client_id}/profile-picture`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo uploaded!");
      fetchClientData(client.client_id);
    } catch { toast.error("Failed to upload photo"); }
    finally { setUploadingPhoto(false); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${API}/client/${client.client_id}/messages`, { booking_id: selectedBooking, message: newMessage });
      setNewMessage("");
      fetchClientData(client.client_id);
      toast.success("Message sent!");
    } catch { toast.error("Failed to send"); }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <Toaster position="top-center" richColors />
        <div className="bg-white p-8 border border-neutral-200 w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="font-serif text-2xl">Sheedtyme2pics</a>
            <p className="text-neutral-500 text-sm mt-2">Client Portal</p>
          </div>
          <Tabs value={authMode} onValueChange={setAuthMode}>
            <TabsList className="w-full mb-6">
              <TabsTrigger value="login" className="flex-1">Login</TabsTrigger>
              <TabsTrigger value="register" className="flex-1">Register</TabsTrigger>
            </TabsList>
            <form onSubmit={handleAuth} className="space-y-4">
              {authMode === "register" && (
                <>
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Name *</label><Input value={authForm.name} onChange={e => setAuthForm({ ...authForm, name: e.target.value })} required className="rounded-none" /></div>
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Phone</label><Input value={authForm.phone} onChange={e => setAuthForm({ ...authForm, phone: e.target.value })} className="rounded-none" /></div>
                  <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Address</label><Input value={authForm.address} onChange={e => setAuthForm({ ...authForm, address: e.target.value })} placeholder="Street Address" className="rounded-none" /></div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={authForm.city} onChange={e => setAuthForm({ ...authForm, city: e.target.value })} placeholder="City" className="rounded-none" />
                    <Input value={authForm.state} onChange={e => setAuthForm({ ...authForm, state: e.target.value })} placeholder="State" className="rounded-none" />
                    <Input value={authForm.zip_code} onChange={e => setAuthForm({ ...authForm, zip_code: e.target.value })} placeholder="ZIP" className="rounded-none" />
                  </div>
                </>
              )}
              <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Email *</label><Input type="email" value={authForm.email} onChange={e => setAuthForm({ ...authForm, email: e.target.value })} required className="rounded-none" /></div>
              <div><label className="text-xs uppercase tracking-widest font-medium mb-1 block">Password *</label><Input type="password" value={authForm.password} onChange={e => setAuthForm({ ...authForm, password: e.target.value })} required className="rounded-none" /></div>
              <Button type="submit" disabled={loading} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none py-6">{loading ? "..." : authMode === "login" ? "Login" : "Create Account"}</Button>
            </form>
          </Tabs>
          <a href="/" className="block text-center mt-6 text-sm text-neutral-500 hover:text-black">← Back to website</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100">
      <Toaster position="top-center" richColors />
      <header className="bg-white border-b px-6 py-4 flex justify-between items-center">
        <div>
          <a href="/" className="font-serif text-xl">Sheedtyme2pics</a>
          <span className="text-neutral-400 ml-2">/ Client Portal</span>
        </div>
        <div className="flex items-center gap-4">
          {profile?.profile_picture_url && <img src={`${BACKEND_URL}${profile.profile_picture_url}`} alt="Profile" className="w-8 h-8 rounded-full object-cover" />}
          <span className="text-sm text-neutral-600">Welcome, {client?.name}</span>
          <Button variant="outline" onClick={handleLogout} className="rounded-none text-xs"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Bookings</TabsTrigger>
            <TabsTrigger value="transactions" className="flex items-center gap-2"><CreditCard className="w-4 h-4" /> Transactions</TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2"><Mail className="w-4 h-4" /> Messages {messages.filter(m => m.sender === "admin" && !m.read).length > 0 && <span className="bg-red-500 text-white text-xs px-2 rounded-full">{messages.filter(m => m.sender === "admin" && !m.read).length}</span>}</TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2"><User className="w-4 h-4" /> Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <div className="bg-white border p-8 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                  <p className="text-neutral-500">No bookings yet</p>
                  <a href="/#booking" className="text-blue-600 hover:underline text-sm mt-2 block">Book your first session →</a>
                </div>
              ) : bookings.map(booking => (
                <div key={booking.id} className="bg-white border p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-medium text-lg">{booking.package}</h3>
                      <p className="text-neutral-500 text-sm">{booking.session_type}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 ${booking.status === "confirmed" || booking.status === "deposit_paid" ? "bg-green-100 text-green-800" : booking.status === "completed" ? "bg-blue-100 text-blue-800" : booking.status === "cancelled" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>{booking.status}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div><span className="text-neutral-500 block">Date</span><strong>{booking.date}</strong></div>
                    <div><span className="text-neutral-500 block">Location</span>{booking.location}</div>
                    <div><span className="text-neutral-500 block">Total</span><strong>${booking.total_estimate}</strong></div>
                    <div><span className="text-neutral-500 block">Remaining Balance</span><strong className={booking.remaining_balance > 0 ? "text-orange-600" : "text-green-600"}>${booking.remaining_balance?.toFixed(2)}</strong></div>
                  </div>
                  {booking.remaining_balance > 0 && (
                    <div className="bg-orange-50 border border-orange-200 p-3 text-sm mb-4">
                      <span className="font-medium">Payment Due:</span> {booking.payment_due_date} — Balance: ${booking.remaining_balance?.toFixed(2)}
                    </div>
                  )}
                  {booking.deposit_paid && (
                    <div className="flex items-center gap-2 text-green-600 text-sm"><Check className="w-4 h-4" /> Deposit Paid: ${booking.deposit_amount}</div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            <div className="bg-white border">
              <div className="border-b p-4">
                <h3 className="font-medium">Transaction History</h3>
                <p className="text-sm text-neutral-500">All payments and charges</p>
              </div>
              <div className="divide-y">
                {transactions.length === 0 ? (
                  <div className="p-8 text-center">
                    <CreditCard className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                    <p className="text-neutral-500">No transactions yet</p>
                  </div>
                ) : transactions.map((t, idx) => (
                  <div key={idx} className="p-4 hover:bg-neutral-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{t.description || t.type}</p>
                        <p className="text-sm text-neutral-500">{new Date(t.date).toLocaleDateString()}</p>
                        {t.card_last4 && <p className="text-xs text-neutral-400 mt-1"><CreditCard className="w-3 h-3 inline mr-1" />{t.card_brand} •••• {t.card_last4}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-lg">${t.amount?.toFixed(2)}</p>
                        <span className={`text-xs px-2 py-1 ${t.status === "succeeded" || t.status === "paid" ? "bg-green-100 text-green-800" : t.status === "failed" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>{t.status}</span>
                      </div>
                    </div>
                    {t.receipt_url && <a href={t.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-2 inline-block">View Receipt →</a>}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="bg-white border">
              <div className="border-b p-4">
                <h3 className="font-medium">Messages with Sheedtyme2pics</h3>
                <p className="text-sm text-neutral-500">Direct communication about your bookings</p>
              </div>
              <div className="h-96 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                    <p className="text-neutral-500">No messages yet</p>
                    <p className="text-sm text-neutral-400">Start a conversation below</p>
                  </div>
                ) : messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender === "client" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-md p-3 ${msg.sender === "client" ? "bg-black text-white" : "bg-neutral-100"}`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className={`text-xs mt-1 ${msg.sender === "client" ? "text-neutral-400" : "text-neutral-500"}`}>{new Date(msg.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t p-4 flex gap-2">
                <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type your message..." className="flex-1 rounded-none" onKeyPress={e => e.key === "Enter" && sendMessage()} />
                <Button onClick={sendMessage} className="bg-black text-white hover:bg-neutral-800 rounded-none"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="profile">
            <div className="bg-white border p-6">
              <h3 className="font-medium text-lg mb-6">My Profile</h3>
              
              {/* Profile Picture */}
              <div className="flex items-center gap-6 mb-8 pb-6 border-b">
                <div className="relative">
                  {profile?.profile_picture_url ? (
                    <img src={`${BACKEND_URL}${profile.profile_picture_url}`} alt="Profile" className="w-24 h-24 rounded-full object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-neutral-200 flex items-center justify-center">
                      <User className="w-10 h-10 text-neutral-400" />
                    </div>
                  )}
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-neutral-800">
                    <Camera className="w-4 h-4" />
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  </label>
                </div>
                <div>
                  <p className="font-medium text-lg">{profile?.name}</p>
                  <p className="text-neutral-500">{profile?.email}</p>
                  {uploadingPhoto && <p className="text-sm text-blue-600">Uploading...</p>}
                </div>
              </div>

              {/* Profile Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest font-medium mb-1 block">Full Name</label>
                    <Input value={profileForm.name || ""} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="rounded-none" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-medium mb-1 block">Phone</label>
                    <Input value={profileForm.phone || ""} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} className="rounded-none" />
                  </div>
                </div>
                
                <div>
                  <label className="text-xs uppercase tracking-widest font-medium mb-1 block">Email</label>
                  <Input value={profile?.email || ""} disabled className="rounded-none bg-neutral-100" />
                  <p className="text-xs text-neutral-500 mt-1">Email cannot be changed</p>
                </div>
                
                <div>
                  <label className="text-xs uppercase tracking-widest font-medium mb-1 block">Street Address</label>
                  <Input value={profileForm.address || ""} onChange={e => setProfileForm({...profileForm, address: e.target.value})} className="rounded-none" />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest font-medium mb-1 block">City</label>
                    <Input value={profileForm.city || ""} onChange={e => setProfileForm({...profileForm, city: e.target.value})} className="rounded-none" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-medium mb-1 block">State</label>
                    <Input value={profileForm.state || ""} onChange={e => setProfileForm({...profileForm, state: e.target.value})} className="rounded-none" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-medium mb-1 block">ZIP Code</label>
                    <Input value={profileForm.zip_code || ""} onChange={e => setProfileForm({...profileForm, zip_code: e.target.value})} className="rounded-none" />
                  </div>
                </div>
                
                <Button onClick={handleProfileUpdate} className="bg-black text-white hover:bg-neutral-800 rounded-none px-8">Save Changes</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Sponsor Page
const SponsorsPage = () => {
  const [tiers, setTiers] = useState({});
  const [adPlacements, setAdPlacements] = useState({});
  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/sponsors/tiers`),
      axios.get(`${API}/advertising/placements`)
    ]).then(([tiersRes, adsRes]) => {
      setTiers(tiersRes.data);
      setAdPlacements(adsRes.data);
    }).catch(() => {});
    
    // Track page view
    const sessionId = sessionStorage.getItem("session_id") || `session_${Date.now()}`;
    axios.post(`${API}/track/pageview`, null, { params: { page: "sponsors", path: "/sponsors", session_id: sessionId } }).catch(() => {});
  }, []);

  const handleSubmit = async (type) => {
    setLoading(true);
    try {
      let endpoint = type === "sponsor" ? "/sponsors/inquiry" : type === "collab" ? "/brand-collaboration" : "/advertising/inquiry";
      await axios.post(`${API}${endpoint}`, formData);
      toast.success("Thank you! We'll be in touch soon.");
      setActiveForm(null);
      setFormData({});
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />
      <header className="bg-black text-white py-20 px-6 text-center">
        <a href="/" className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white mb-4 inline-block">← Back to Website</a>
        <h1 className="font-serif text-4xl md:text-6xl mb-4">Partner With Us</h1>
        <p className="text-neutral-400 max-w-2xl mx-auto">Collaborate with Sheedtyme2pics to reach an engaged audience of sports enthusiasts, families, and creative professionals.</p>
      </header>

      {/* Sponsor Tiers */}
      <section className="py-20 px-6 bg-neutral-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-3xl text-center mb-4">Sponsorship Tiers</h2>
          <p className="text-neutral-500 text-center mb-12">Choose a tier that fits your brand's goals</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(tiers).map(([key, tier]) => (
              <div key={key} className={`border p-6 bg-white ${key === "gold" || key === "platinum" ? "border-yellow-400 ring-2 ring-yellow-100" : ""}`}>
                <h3 className="font-serif text-xl mb-2">{tier.name}</h3>
                <p className="text-3xl font-serif mb-1">${tier.price}<span className="text-sm text-neutral-500">/{tier.duration}</span></p>
                <ul className="space-y-2 mt-4 mb-6">
                  {tier.features?.map((f, i) => <li key={i} className="text-sm flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{f}</li>)}
                </ul>
                <Button onClick={() => { setActiveForm("sponsor"); setFormData({ ...formData, tier: key }); }} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none">Get Started</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advertising */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-3xl text-center mb-4">Advertising Spots</h2>
          <p className="text-neutral-500 text-center mb-12">Premium placement options for your business</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(adPlacements).map(([key, ad]) => (
              <div key={key} className="border p-6 flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-lg">{ad.name}</h3>
                  <p className="text-sm text-neutral-500">{ad.description}</p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-xl">${ad.price_month}<span className="text-sm text-neutral-500">/mo</span></p>
                  <Button variant="outline" onClick={() => { setActiveForm("ad"); setFormData({ ...formData, ad_placement: key }); }} className="mt-2 rounded-none">Inquire</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Collaborations */}
      <section className="py-20 px-6 bg-neutral-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl mb-4">Brand Collaborations</h2>
          <p className="text-neutral-500 mb-8">Interested in product placement, content creation, or ambassador partnerships?</p>
          <Button onClick={() => setActiveForm("collab")} className="bg-black text-white hover:bg-neutral-800 rounded-none px-8 py-6">Let's Collaborate</Button>
        </div>
      </section>

      {/* Contact Form Dialog */}
      <Dialog open={activeForm !== null} onOpenChange={(open) => !open && setActiveForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {activeForm === "sponsor" ? "Sponsor Inquiry" : activeForm === "ad" ? "Advertising Inquiry" : "Brand Collaboration"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder={activeForm === "collab" ? "Brand Name *" : "Company Name *"} value={formData.company_name || formData.brand_name || ""} onChange={e => setFormData({...formData, [activeForm === "collab" ? "brand_name" : activeForm === "ad" ? "business_name" : "company_name"]: e.target.value})} className="rounded-none" />
            <Input placeholder="Contact Name *" value={formData.contact_name || ""} onChange={e => setFormData({...formData, contact_name: e.target.value})} className="rounded-none" />
            <Input type="email" placeholder="Email *" value={formData.email || ""} onChange={e => setFormData({...formData, email: e.target.value})} className="rounded-none" />
            <Input placeholder="Phone" value={formData.phone || ""} onChange={e => setFormData({...formData, phone: e.target.value})} className="rounded-none" />
            {activeForm === "collab" && (
              <>
                <Input placeholder="Instagram Handle" value={formData.instagram || ""} onChange={e => setFormData({...formData, instagram: e.target.value})} className="rounded-none" />
                <Select onValueChange={v => setFormData({...formData, collaboration_type: v})}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Collaboration Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Product Placement</SelectItem>
                    <SelectItem value="event">Event Coverage</SelectItem>
                    <SelectItem value="content">Content Creation</SelectItem>
                    <SelectItem value="ambassador">Brand Ambassador</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            <Textarea placeholder="Tell us about your goals..." value={formData.message || ""} onChange={e => setFormData({...formData, message: e.target.value})} className="rounded-none" rows={4} />
            <Button onClick={() => handleSubmit(activeForm)} disabled={loading} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none">{loading ? "Sending..." : "Submit Inquiry"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Contract Signing Page
const ContractPage = () => {
  const [bookingId, setBookingId] = useState(null);
  const [contract, setContract] = useState(null);
  const [policies, setPolicies] = useState({});
  const [booking, setBooking] = useState(null);
  const [agreements, setAgreements] = useState({ model_release: false, copyright: false, cancellation: false, rescheduling: false });
  const [signatureData, setSignatureData] = useState("");
  const [typedSignature, setTypedSignature] = useState("");
  const [loading, setLoading] = useState(false);
  const [signed, setSigned] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("booking");
    if (id) {
      setBookingId(id);
      axios.get(`${API}/booking/${id}/contract`).then(res => {
        setContract(res.data.contract);
        setPolicies(res.data.policies);
        setBooking(res.data.booking);
        setSigned(res.data.is_signed);
      }).catch(() => toast.error("Could not load contract"));
    }
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    setSignatureData(canvas.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData("");
  };

  const handleSign = async () => {
    if (!agreements.model_release || !agreements.copyright || !agreements.cancellation || !agreements.rescheduling) {
      toast.error("Please agree to all policies");
      return;
    }
    if (!signatureData && !typedSignature) {
      toast.error("Please provide your signature");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/booking/${bookingId}/sign-contract`, {
        model_release_agreed: agreements.model_release,
        copyright_agreed: agreements.copyright,
        cancellation_policy_agreed: agreements.cancellation,
        rescheduling_policy_agreed: agreements.rescheduling,
        signature_data: signatureData,
        signature_typed: typedSignature
      });
      toast.success("Contract signed successfully!");
      setSigned(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to sign contract"); }
    finally { setLoading(false); }
  };

  if (!bookingId) {
    return <div className="min-h-screen flex items-center justify-center"><p className="text-neutral-500">No booking specified</p></div>;
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="bg-white p-8 border max-w-md text-center">
          <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-2">Contract Signed!</h1>
          <p className="text-neutral-500 mb-6">Thank you for signing. You'll receive a confirmation email shortly.</p>
          <a href="/" className="text-sm text-neutral-500 hover:text-black">← Return to Website</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Toaster position="top-center" richColors />
      <header className="bg-black text-white py-8 px-6 text-center">
        <h1 className="font-serif text-2xl">Session Agreement</h1>
        {booking && <p className="text-neutral-400 mt-2">{booking.name} • {booking.date}</p>}
      </header>

      <div className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Policies */}
        {[
          { key: "model_release", title: "Model Release Agreement", content: policies.model_release },
          { key: "copyright", title: "Copyright Agreement", content: policies.copyright },
          { key: "cancellation", title: "Cancellation Policy", content: policies.cancellation },
          { key: "rescheduling", title: "Rescheduling Policy", content: policies.rescheduling }
        ].map(policy => (
          <div key={policy.key} className="bg-white border p-6">
            <h3 className="font-medium text-lg mb-4">{policy.title}</h3>
            <div className="bg-neutral-50 p-4 max-h-48 overflow-y-auto mb-4">
              <pre className="text-sm whitespace-pre-wrap font-sans">{policy.content}</pre>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={agreements[policy.key]} onCheckedChange={checked => setAgreements({...agreements, [policy.key]: checked})} />
              <span className="text-sm">I have read and agree to the {policy.title}</span>
            </label>
          </div>
        ))}

        {/* Signature */}
        <div className="bg-white border p-6">
          <h3 className="font-medium text-lg mb-4">Your Signature</h3>
          <div className="mb-4">
            <p className="text-sm text-neutral-500 mb-2">Draw your signature:</p>
            <canvas ref={canvasRef} width={400} height={150} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} className="border w-full bg-white cursor-crosshair" style={{touchAction: "none"}} />
            <Button variant="outline" onClick={clearSignature} className="mt-2 rounded-none text-sm">Clear</Button>
          </div>
          <div>
            <p className="text-sm text-neutral-500 mb-2">Or type your full name:</p>
            <Input value={typedSignature} onChange={e => setTypedSignature(e.target.value)} placeholder="Type your full legal name" className="rounded-none font-serif text-xl" />
          </div>
        </div>

        <Button onClick={handleSign} disabled={loading} className="w-full bg-black text-white hover:bg-neutral-800 rounded-none py-6 text-lg">
          {loading ? "Signing..." : "Sign Contract"}
        </Button>
      </div>
    </div>
  );
};

// Client Gallery Page
const GalleryPage = () => {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [accessCode, setAccessCode] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = window.location.pathname.split("/gallery/")[1] || params.get("code");
    if (code) {
      setAccessCode(code);
      axios.get(`${API}/gallery/${code}`).then(res => {
        setGallery(res.data);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        toast.error("Gallery not found or expired");
      });
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="bg-white p-8 border max-w-md text-center">
          <FolderOpen className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-2">Enter Gallery Code</h1>
          <Input value={accessCode} onChange={e => setAccessCode(e.target.value)} placeholder="Enter your access code" className="rounded-none mb-4" />
          <Button onClick={() => window.location.href = `/gallery/${accessCode}`} className="w-full bg-black text-white rounded-none">View Gallery</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />
      <header className="bg-black text-white py-12 px-6 text-center">
        <h1 className="font-serif text-3xl mb-2">{gallery.title}</h1>
        <p className="text-neutral-400">{gallery.photos?.length || 0} photos</p>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.photos?.map((photo, i) => (
            <div key={i} onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }} className="cursor-pointer group">
              <div className="aspect-square overflow-hidden">
                <img src={`${BACKEND_URL}${photo.watermarked_url || photo.url}`} alt={`Photo ${i + 1}`} className="w-full h-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center bg-neutral-50 p-8">
          <h2 className="font-serif text-2xl mb-4">Love Your Photos?</h2>
          <p className="text-neutral-500 mb-6">Purchase prints or digital downloads to keep these memories forever.</p>
          <a href="/#shop"><Button className="bg-black text-white hover:bg-neutral-800 rounded-none px-8">Shop Prints</Button></a>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl w-full h-[90vh] p-0 bg-black border-none">
          <DialogHeader className="sr-only"><DialogTitle>Photo View</DialogTitle></DialogHeader>
          <div className="relative w-full h-full flex items-center justify-center">
            {gallery.photos?.[lightboxIndex] && (
              <img src={`${BACKEND_URL}${gallery.photos[lightboxIndex].watermarked_url || gallery.photos[lightboxIndex].url}`} alt="Full view" className="max-w-full max-h-full object-contain" />
            )}
            <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 text-white hover:text-neutral-300"><X className="w-8 h-8" /></button>
            <button onClick={() => setLightboxIndex(prev => prev > 0 ? prev - 1 : gallery.photos.length - 1)} className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 p-2 rounded-full"><ChevronDown className="w-8 h-8 rotate-90" /></button>
            <button onClick={() => setLightboxIndex(prev => prev < gallery.photos.length - 1 ? prev + 1 : 0)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 p-2 rounded-full"><ChevronDown className="w-8 h-8 -rotate-90" /></button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    
    // Get or create session ID
    let sessionId = sessionStorage.getItem("session_id");
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("session_id", sessionId);
    }
    
    // Track page view
    axios.post(`${API}/track/pageview`, null, { 
      params: { page: "home", path: window.location.pathname, session_id: sessionId } 
    }).catch(() => {});
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <CartProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <Header scrolled={scrolled} />
        <main>
          <Hero />
          <Services />
          <Portfolio />
          <Booking />
          <Shop />
          <Contact />
        </main>
        <Footer />
      </div>
    </CartProvider>
  );
};

// App
function App() {
  return (
    <BrowserRouter>
      <CartProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/portal" element={<ClientPortal />} />
          <Route path="/sponsors" element={<SponsorsPage />} />
          <Route path="/contract" element={<ContractPage />} />
          <Route path="/gallery/:code" element={<GalleryPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
        </Routes>
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
